/** The drawing surface. A display list in, an AutoCAD-looking drawing out.
 *
 * ⚠⚠ **THE DRAWING LIVES HERE, NOT ON THE SERVER, AND THAT IS THE BASE DECISION.** `docs/GAP.md`
 * §4 measured it rather than argued it: an object snap that redraws a rubber band at 60 fps has
 * a **16 millisecond** budget and a round trip on this box is 5-50 ms. So the whole display list
 * arrives once and every pan, zoom and snap is local. `views.py` says the same thing from the
 * other side.
 *
 * ⚠⚠ **BATCHED BY COLOUR, NOT DRAWN PER ENTITY.** A real client plan is 50,000 polylines and
 * `beginPath`/`stroke` per polyline spends its whole frame budget in state changes — measured on
 * the office corpus, one drawing is 259,561 entities. One path per colour, stroked once, is the
 * difference between a drawing that pans and a slideshow.
 *
 * ⚠ Coordinates are FLAT `[x, y, x, y, ...]` — see `display.py`. Reading them as objects would
 * be tens of megabytes of punctuation for the same picture.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { penWidth } from "./pen";
import { anchorOf, halignOf, TWO_POINT } from "./textjust";

import { ACAD_BG, aciToCss, rgbToCss } from "./aci";
import { circleThrough, rectCorners } from "./commands";
import { SnapIndex, type SnapHit } from "./snap";
import { unchangedBefore } from "./delta";
import { publishUX } from "./uxprobe";
// ⚠ TYPE-only, so the dynamic `gpu/build` chunk stays out of the main bundle — see the note at
// the `gpuFills` assignment, which is why that constant is carried on a ref instead of imported.
import type { SceneTimes } from "./gpu/build";

/** ⚠ The kinds a double-click opens the editor on. Same set as the server's `TEXTUAL`. */
const TEXTUAL = new Set(["TEXT", "MTEXT", "ATTRIB", "ATTDEF"]);

export interface DisplayLayer {
  name: string; aci: number; rgb: number | null;
  on: boolean; frozen: boolean; lw: number;
}
export interface DisplayList {
  layers: DisplayLayer[];
  /** ⚠⚠ **THE PEN WEIGHT AN ENTITY OVERRIDES ITS LAYER WITH**, keyed by owner handle, in
   *  hundredths of a millimetre. Only the entities that say something other than BYLAYER are
   *  here — 24.1% of them across this office's drawings — so an ABSENT key means "ask the
   *  layer", which is what `layers[i].lw` is for. Optional: a cache written before this
   *  existed has none, and then every entity simply follows its layer. */
  elw?: Record<string, number>;
  /** `[layerIndex, aci, [x, y, x, y, ...], ownerHandle, ownerKind, linetypeIndex]`.
   *  ⚠ `linetypeIndex` is an index into `ltypes`; 0 is solid. Optional in the tuple only in the
   *  sense that a cache written before FORMAT 16 has five elements — `?? 0` covers that, and
   *  it degrades to "everything is solid", which is what the app did before. */
  polys: [number, number, number[], string, string, number?][];
  /** Areas that are PAINTED rather than stroked — `WIPEOUT` and the 2D `SOLID`.
   *  `[layerIndex, aci, points, ownerHandle, ownerKind, mask, at, ta]`.
   *  ⚠ `mask` 1 paints the PAPER colour: a wipeout has no colour of its own, it hides.
   *  ⚠ `at` is the index in `polys` this fill sits at in DRAW ORDER — see the banded paint.
   *  ⚠ `ta` is its index in `texts`, which is a SEPARATE channel painted after everything —
   *  without it a wipeout hides the geometry under a note and leaves the note on top.
   *  Optional because a cache written before FORMAT 10 has none, and an old file must degrade
   *  to "wipeouts are outlines again", not to a crash. */
  /** ⚠ The last member is the GRADIENT: `[name, "#rrggbb", "#rrggbb" | null, angle]`, or null
   *  for an ordinary solid. Added at FORMAT 27 — before it, every gradient hatch in every
   *  drawing painted as one flat colour here while the DXF held the right two. */
  fills?: [number, number, number[], string, string, number, number, number,
           [string, string, string | null, number] | null, number[][] | null][];
  /** `[layer, aci, x, y, height, rotation, text, owner, ownerKind, width, attachment, style]`.
   *  ⚠ `width < 0` means a single-line TEXT drawn on its baseline; `width >= 0` means an MTEXT,
   *  whose point is the corner of a BOX and which wraps to `width` when that is non-zero.
   *  ⚠ `style` indexes `tstyles`; a cache written before FORMAT 18 has no twelfth element and
   *  `?? 0` degrades it to the default, which is what this canvas drew before. */
  //: ⚠ 13 and 14 are the SECOND POINT that an ALIGNED or FITted text spans — FORMAT 34, and
  //: present only on those two justifications. Optional both ways: an older list has neither
  //: and the canvas falls back to the anchor it used before.
  texts: [number, number, number, number, number, number, string, string, string,
          number, number, number?, number?, number?][];
  /** The paper layouts in this list — empty in model space. See `display.py`'s `_sheet_of`. */
  sheets?: Sheet[];
  /** ⚠ Circle and arc centres, flat `[layerIndex, x, y, radius, ...]` — see `display.py`.
   *  Optional because a cache written before FORMAT 3 has none, and a missing centre must
   *  degrade to "no centre snap", not to a crash on an old file. */
  cen?: number[];
  extents: [number, number, number, number];
  counts: { polys: number; texts: number; points: number; entities: number;
            circles?: number; fills?: number; carried?: number };
  /** Entities that are in the drawing and CANNOT be drawn, by kind — `REGION`, `3DSOLID`,
   *  `BODY`. ⚠ Not a loss: they export exactly as they arrived. Their geometry is an ACIS blob
   *  and ezdxf's own SAT parser refuses AutoCAD's ASM output, so nothing here can turn one into
   *  a line. Reported so a blank space on screen has a name attached to it.
   *  Optional: a cache written before FORMAT 11 has none. */
  carried_by_kind?: Record<string, number>;
  /** Attribute VALUES per block reference, `{ownerHandle: [[tag, value], ...]}`.
   *  ⚠ An attribute already draws — it arrives in `texts` owned by the INSERT — but as a bare
   *  string. "MEETING" on screen is not "the ROOM attribute is MEETING", and the palette cannot
   *  offer to change something it cannot name. Optional: a cache before FORMAT 12 has none. */
  atts?: Record<string, [string, string][]>;
  /** POINT entities — `[layerIndex, aci, x, y, owner, ownerKind]`.
   *  ⚠⚠ A CHANNEL OF ITS OWN BECAUSE A POINT HAS NO EXTENT. It flattens to one vertex, and a
   *  poly run of one vertex draws nothing — which is exactly what happened: POINT saved,
   *  round-tripped, gripped, and was invisible. AutoCAD sizes it by PDSIZE, which is a
   *  PERCENTAGE OF THE SCREEN when negative, so only the canvas can decide how big it is.
   *  Optional: a cache written before FORMAT 13 has none. */
  marks?: [number, number, number, number, string, string][];
  /** XLINE and RAY — `[layerIndex, aci, x, y, dx, dy, halfLine, owner, ownerKind]`.
   *  ⚠ Infinite, so they are clipped to the view every frame rather than stored as a path.
   *  `halfLine` 1 is a RAY: it runs one way from its base point only. */
  rays?: [number, number, number, number, number, number, number, string, string][];
  /** Owners whose `polys` run is a PICKING CENTRELINE and must not be stroked — a wide polyline
   *  is painted from `fills` instead. ⚠ The same arrangement a wipeout uses from the other
   *  side: the outline is in `polys` so `hit()` and the snaps can find it, and skipping the
   *  stroke is what stops it drawing over the thing it is. */
  widened?: number[];
  /** INDICES into `polys` that are an underlay's FRAME — AutoCAD's `FRAME` variable hides
   *  these. ⚠ Suppressed from the STROKE only; the poly must stay, or the underlay cannot be
   *  picked. */
  uframes?: number[];
  /** ⚠⚠ **DASH ARRAYS IN DRAWING UNITS, WITH `$LTSCALE` AND EACH ENTITY'S OWN SCALE ALREADY
   *  MULTIPLIED IN.** Index 0 is solid. Measured before this existed: **59% of the drawings
   *  imported here use a non-continuous linetype and every one of them drew as a solid line** —
   *  the `LTYPE` table came across whole from the first commit and nothing ever asked it for a
   *  pattern. A hidden edge drawn solid reads as a real edge on a shop drawing. */
  ltypes?: number[][];
  /** The drawing's `$LTSCALE`, reported so the palette can say what produced what is on screen.
   *  ⚠ Measured across these drawings: 10.0 in 39 of 80, 1.0 in 37, and three at 200/400/500. */
  ltscale?: number;
  /** ⚠⚠ **INSERTION POINTS, FOR AutoCAD'S `INSERTION` SNAP** — blocks, texts and attributes.
   *  Flat `[layerIndex, x, y, ...]`, same shape as `cen`. A door block's insertion point is its
   *  hinge, and no stroke it draws passes through it, so nothing else in the display list can
   *  stand in for it. Optional: a cache before FORMAT 17 has none. */
  ins?: number[];
  /** ⚠⚠ **THE FONT AND THE WIDTH FACTOR — `[cssFontStack, weight, italic, widthFactor,
   *  obliqueDeg]`, slot 0 the default.** Measured before this existed: **49.2% of the text in
   *  this office's drawings carries a width factor that is not 1.0**, almost all of it 0.7, and
   *  every one of them drew at 1.0 in Arial — about 43% too wide. On drawing 50, whose 2,619 of
   *  2,751 texts are condensed, `CÔNG SUẤT TỔNG:  3000` came out as `CÔNG SUẤT TỔN3000`.
   *  ⚠ The PDF plotter has always been right — it goes through ezdxf's own frontend — so this
   *  was the screen disagreeing with the deliverable. Optional: a cache before FORMAT 18. */
  tstyles?: [string, number, number, number, number][];
  /** ⚠⚠ **RASTER IMAGES — `[layer, x0,y0, x1,y1, x2,y2, x3,y3, defHandle, name, owner]`.**
   *  `IMAGE` is in 21.4% of this office's drawings (82,527 of them) and every one drew as an
   *  empty rectangle — the frame was right, the picture was simply absent, which reads as a bug
   *  rather than as a missing file.
   *  ⚠ `defHandle` is `""` when there are no pixels here, and measured on the 93 IMAGEDEF
   *  records stored on this box **not one of their files exists anywhere reachable** — they are
   *  a drafter's own D: drive, photographs from 2008, dead in AutoCAD too. Those draw as the
   *  frame WITH THE FILE NAME IN IT, which is what AutoCAD does.
   *  ⚠ Four corners, not an insert and a size: an IMAGE can be rotated and non-uniformly
   *  scaled. Optional — a cache written before FORMAT 19 has none. */
  /** UNRESOLVED underlays — `[layerIndex, x, y, name, owner]`. ⚠ A channel of its own because
   *  there is no FRAME: an underlay with no boundary path extends over its whole page, and the
   *  page size lives in a PDF this app has never seen. See `display._underlay_bounds`. */
  underlays?: [number, number, number, string, string][];
  /** ⚠ The row then carries FADE (0–100 — an IMAGE's group 281 and an underlay's 282), the
   *  MONOCHROME flag, the entity KIND, and the CLIP as a flat world polygon (`[]` if none).
   *  Shared channel because both are painted the same way; the kind is there so anything that
   *  must distinguish them can, WITHOUT guessing from the file name — an IMAGE called
   *  `plan.pdf` is not an underlay.
   *  ⚠⚠ The clip is a MASK, not a re-fit of the corners: re-fitting squashes the whole page
   *  into the rectangle instead of cropping it, which is what the first version did. */
  images?: [number, number, number, number, number, number, number, number, number,
            string, string, string, number, number, string, number[]][];
}

/** What a click landed on. ⚠ The OWNER — the top-level entity — not the primitive under the
 *  cursor. Clicking one line of a title block selects the block reference, which is what
 *  AutoCAD does and what a drafter expects to then move. */
export interface Pick { handle: string; kind: string; layer: string; aci: number }

/** ⚠ AutoCAD's pickbox, in SCREEN pixels rather than drawing units — a pick tolerance that
 *  shrinks as you zoom in is a pick tolerance that stops working at the scale where precision
 *  actually matters. Chrome's own hit-testing works the same way. */
let PICK_PX = 6;

/** ⚠⚠⚠ **THE DRAFTER'S OWN HAND, AND UNTIL NOW IT WAS A CONSTANT.** `PICKBOX`, `APERTURE`,
 *  `GRIPSIZE` and `ZOOMFACTOR` are AutoCAD system variables kept in the PROFILE rather than in
 *  the drawing — one drafter's pick target is not part of the building. Every one of them
 *  already did its job here at a fixed value; what was missing was AutoCAD's way of changing it,
 *  which is to type its name. `sysvars.USER_SCOPED` is the server's half.
 *  ⚠ Module-level, deliberately: `hit()` reads the pickbox on every mouse move and a prop
 *  through three components would be re-read on every one of them. `Studio` calls this once
 *  when the profile arrives and once per SETVAR.
 *  ⚠ Clamped to AutoCAD's own ranges — a pickbox of 0 makes the drawing unclickable and one of
 *  500 makes every click ambiguous, and AutoCAD refuses both. */
export function setHandVars(v: { PICKBOX?: number; APERTURE?: number; GRIPSIZE?: number;
                                 ZOOMFACTOR?: number; HIGHLIGHT?: number }): void {
  if (typeof v.PICKBOX === "number") PICK_PX = Math.min(50, Math.max(0, v.PICKBOX));
  if (typeof v.APERTURE === "number") APERTURE_PX = Math.min(50, Math.max(1, v.APERTURE));
  if (typeof v.GRIPSIZE === "number") GRIP_PX = Math.min(255, Math.max(1, v.GRIPSIZE));
  if (typeof v.ZOOMFACTOR === "number") ZOOM_FACTOR = Math.min(100, Math.max(3, v.ZOOMFACTOR));
  if (typeof v.HIGHLIGHT === "number") HIGHLIGHT = v.HIGHLIGHT !== 0;
}

/** HIGHLIGHT — 0 selects an object without drawing it selected. ⚠ AutoCAD's own use for this is
 *  a huge selection on a slow machine, where the highlight costs more than the command; the
 *  grips still appear, because they are how you edit it. */
let HIGHLIGHT = true;

/** GRIPSIZE — the grip square's half-size in screen pixels. AutoCAD's default is 5. */
let GRIP_PX = 5;

/** Model-space bounds of every poly, and of the drawing, built ONCE per display list.
 *
 *  ⚠⚠ **THIS IS WHAT MAKES ZOOMING IN CHEAP, AND IT HAS TO BE PRECOMPUTED TO BE WORTH IT.**
 *  The paint pass cannot ask "is this poly on screen?" by walking its points — that is the same
 *  work as drawing it. Measured on drawing 1158 (414,943 polys) before this existed: at extents
 *  the 2D canvas took 66.6 ms a frame, and zoomed in 30x — a view holding about a nine-hundredth
 *  of the area — it took 49.9 ms. A drafter spends the day zoomed into a detail and was paying
 *  for the whole building on every frame of it.
 *
 *  ⚠ `Float64Array`, not an array of objects: 4 numbers per poly, contiguous, so the per-frame
 *  test is four loads and four compares and no allocation at all.
 */
interface PolyStyle { css: string; lt: number; lw: number }

//: Spare rows kept on the end of the bounds array, so an APPEND writes into it instead of
//: allocating a new one. ⚠ Same idea as `gpu/build.ts`'s instance slack and for the same reason:
//: on `ysl-hn-center-2025` this array is 1,511,116 x 4 x 8 = **48 MB**, and a drafter drawing a
//: line should not make 48 MB of garbage.
const BOUNDS_SLACK = 4096;

/** Every poly's bounding box, and the drawing's.
 *
 * ⚠⚠⚠ **THIS WAS 130 ms OF EVERY EDIT ON THE HEAVIEST DRAWING, AND IT DREW NOTHING.** It is
 * memoised on the display list's IDENTITY, which changes on every edit, so one new line made it
 * walk all 1,511,116 polys and all 3,417,092 of their points again — and allocate 48 MB. The
 * frame reported `polysDrawn 0, textsDrawn 0, selMs 0` and `geomMs 130`, with the GL backend's
 * own `cpuMs` at 1: a frame that drew nothing, for 130 ms.
 *
 * ⇒ `applyDelta` records the index below which every row is the SAME OBJECT (see
 * `delta.unchangedBefore`), so the boxes below it are still right and only the tail is folded.
 * ⚠ `all` is still folded over EVERY box, because a DELETE can only make the drawing smaller and
 * growing the old `all` would leave it too large for ever. That pass is four loads per poly and
 * no points at all.
 * ⚠ `prev` is reused IN PLACE when it has the room. Nothing else holds it: `bnds.current` is the
 * only reference and the caller is replacing it.
 */
function polyBounds(dl: DisplayList,
                    prev?: { b: Float64Array; n: number } | null): { b: Float64Array;
                                                                     n: number;
                                                                     all: number[] } {
  const n = dl.polys.length;
  const mark = prev ? Math.min(unchangedBefore(dl.polys) ?? 0, prev.n, n) : 0;
  let b: Float64Array;
  if (prev && mark > 0 && prev.b.length >= n * 4) {
    b = prev.b;                                   // ⚠ in place — see the note above
  } else {
    b = new Float64Array((n + BOUNDS_SLACK) * 4);
    if (prev && mark > 0) b.set(prev.b.subarray(0, mark * 4));
  }
  for (let k = mark; k < n; k++) {
    const pts = dl.polys[k][2];
    let xi = Infinity, yi = Infinity, xa = -Infinity, ya = -Infinity;
    for (let i = 0; i + 1 < pts.length; i += 2) {
      const x = pts[i], y = pts[i + 1];
      if (x < xi) xi = x;
      if (x > xa) xa = x;
      if (y < yi) yi = y;
      if (y > ya) ya = y;
    }
    b[k * 4] = xi; b[k * 4 + 1] = yi; b[k * 4 + 2] = xa; b[k * 4 + 3] = ya;
  }
  let axi = Infinity, ayi = Infinity, axa = -Infinity, aya = -Infinity;
  for (let k = 0; k < n; k++) {
    const o = k * 4;
    if (b[o] < axi) axi = b[o];
    if (b[o + 1] < ayi) ayi = b[o + 1];
    if (b[o + 2] > axa) axa = b[o + 2];
    if (b[o + 3] > aya) aya = b[o + 3];
  }
  //: ⚠ Where the fold started, published so "still 130 ms" is a number with a cause attached.
  //: `0` means it rebuilt every box, which is what this exists to avoid.
  //: ⚠⚠ **AND A COUNT, BECAUSE A MARK ALONE CANNOT SAY WHETHER IT RAN.** `__pdcadBoundsFrom`
  //: read `0` after every ZOOM on the heaviest drawing, which reads as "it re-folds 1.5 million
  //: boxes every time" — and would have sent a session rewriting a cache that was already
  //: hitting. It is a value from the LAST run, and a run that never happened leaves the previous
  //: one in place. The same trap `__pdcadScene` set an hour earlier, where the scene stats were
  //: identical across fourteen ZOOMs because the scene was never rebuilt at all.
  const w = window as unknown as Record<string, unknown>;
  w.__pdcadBoundsRuns = ((w.__pdcadBoundsRuns as number) ?? 0) + 1;
  w.__pdcadBoundsFrom = mark;
  w.__pdcadBoundsN = n;
  return { b, n, all: [axi, ayi, axa, aya] };
}

/** The stroke style of every poly, resolved ONCE per display list instead of once per frame.
 *
 *  ⚠⚠ **THE PER-POLY COST WAS THE FRAME, NOT THE POINTS.** Drawing 1158 holds 414,943 polys over
 *  1,202,368 points — 2.9 points each, so almost every one is a single line segment and the
 *  work is per-OBJECT overhead, not per-vertex. The batching loop resolved a colour and built a
 *  template-string key `${css}|${lt}|${lw}` for each of them, every frame: 415k string
 *  allocations and 415k Map lookups to discover the handful of distinct styles a drawing
 *  actually uses.
 *
 *  ⚠ Keyed on everything that can change a style — the list, the ink, whether pen weights are
 *  shown, and the per-entity weight overrides — so a colour change or a LWDISPLAY toggle
 *  rebuilds it. Layer ON/FROZEN is deliberately NOT in here: it is checked per frame, because a
 *  layer toggle must take effect without rebuilding the table.
 */
/** ⚠⚠⚠ **AND THIS ONE WAS THE OTHER 130 ms.** It is memoised on the display list's IDENTITY,
 *  which changes on every edit, so one new line rebuilt the style of all 1,511,116 polys —
 *  including a template-string key and a `Map` lookup EACH, which is 1.5 million string
 *  allocations to discover the handful of distinct styles a drawing actually uses. The comment
 *  above records moving this off the per-FRAME path for exactly that reason; per-EDIT is the
 *  same bill, once a drafter is drawing rather than panning.
 *  ⇒ Same mark as `polyBounds` (`delta.unchangedBefore`): the rows below it are the same objects
 *  on the same layers, so their style ids are still right and only the tail is resolved.
 *  ⚠ `defs` and `seen` are CARRIED, not rebuilt — an id already handed out must keep meaning the
 *  same style, or every untouched row's `ids[k]` would point at the wrong one. They grow only
 *  when a genuinely new style appears, which is bounded by the drawing.
 *  ⚠ `ink`, `lwt` and `elw` decide a style, so the caller rebuilds from zero when any of them
 *  moves; this only ever extends a table built under the same three. */
function polyStyles(dl: DisplayList, ink: boolean, lwt: boolean,
                    elw: Record<string, number>,
                    prev?: { ids: Int32Array; defs: PolyStyle[];
                             seen: Map<string, number>; n: number } | null):
                    { ids: Int32Array; defs: PolyStyle[]; seen: Map<string, number>; n: number } {
  const n = dl.polys.length;
  const mark = prev ? Math.min(unchangedBefore(dl.polys) ?? 0, prev.n, n) : 0;
  let ids: Int32Array;
  if (prev && mark > 0 && prev.ids.length >= n) {
    ids = prev.ids;                               // ⚠ in place — `sty.current` is the only holder
  } else {
    ids = new Int32Array(n + (mark > 0 ? BOUNDS_SLACK : 0));
    if (prev && mark > 0) ids.set(prev.ids.subarray(0, mark));
  }
  const defs: PolyStyle[] = prev && mark > 0 ? prev.defs : [];
  const seen = prev && mark > 0 ? prev.seen : new Map<string, number>();
  for (let k = mark; k < n; k++) {
    const [li, aci, , owner, , lt] = dl.polys[k];
    const ly = dl.layers[li];
    if (!ly) { ids[k] = -1; continue; }
    const css = aciToCss(aci, ly.aci, ly.rgb, ink);
    // ⚠ The entity's own weight if it has one, otherwise its layer's — the order AutoCAD
    // resolves in, and the reason `elw` holds only the overrides.
    const lw = lwt ? (elw[owner] ?? ly.lw ?? -1) : -1;
    const key = `${css}|${lt ?? 0}|${lw}`;
    let id = seen.get(key);
    if (id === undefined) { id = defs.length; seen.set(key, id); defs.push({ css, lt: lt ?? 0, lw }); }
    ids[k] = id;
  }
  //: ⚠ Where the resolve started, published for the same reason `__pdcadBoundsFrom` is.
  (window as unknown as Record<string, unknown>).__pdcadStylesFrom = mark;
  return { ids, defs, seen, n };
}

/** ZOOMFACTOR — how far one wheel notch zooms. ⚠ AutoCAD's scale, 3 to 100, and its meaning:
 *  the number is a PERCENTAGE-ish step, not a multiplier. 60 (the default) is about 1.15x per
 *  notch, which is what this canvas has always used. */
let ZOOM_FACTOR = 60;

/** Model space -> screen. ⚠ `y` is NEGATED: DXF counts up, a canvas counts down. */
/** ⚠ `rot` is AutoCAD's rotated view — what `UCSFOLLOW` produces. RADIANS, and OPTIONAL: a
 *  drawing at the world UCS never has one, and `undefined` costs nothing per frame. */
export interface View { x: number; y: number; scale: number; rot?: number }

export interface CanvasApi {
  zoomExtents: () => void;
  /** REGEN — throw the cached scene away and build the whole picture again.
   *
   *  ⚠⚠ **IT EXISTS BECAUSE THIS APP DOES NOT NEED ONE, AND "we don't need one" IS NOT A
   *  MEASUREMENT.** `handlers.ts` answers REGEN with *"the canvas redraws continuously; there is
   *  nothing to regenerate"* — true, and it left `qa/bench_actions.py` with a blank cell beside
   *  a BricsCAD column that has a real number in it. The comparable work is the one thing REGEN
   *  means here: `buildScene` over the WHOLE display list instead of a patch, then the upload
   *  and the frame. Dropping the cached scene is what forces that — see `sceneRef`. */
  regen: () => void;
  zoomBy: (f: number) => void;
  /** ZOOM Window. Returns false for a window with no area — a double click, not a request to
   *  zoom infinitely far in. */
  fitBox: (x0: number, y0: number, x1: number, y1: number) => boolean;
  /** ZOOM Center, Left and Right — a point and the view HEIGHT in drawing units.
   *
   *  ⚠⚠ **THE ANCHORING IS DONE HERE BECAUSE ONLY THIS KNOWS HOW MANY PIXELS WIDE IT IS.** Left
   *  and Right take the LOWER corner, so turning a height into the matching width is the view
   *  maths — and `fitBox` already records why a caller must not write that a second time.
   *  ⚠ Returns false for a height of zero rather than dividing by it. */
  zoomTo: (x: number, y: number, height: number,
           anchor: "center" | "left" | "right") => boolean;
  view: View;
  /** Put the view back where it was — AutoCAD's ZOOM Previous.
   *  ⚠⚠ **PUBLISHED TO READ AND NOT TO SET WAS THE WHOLE REASON ZOOM PREVIOUS COULD NOT EXIST.**
   *  The studio can watch every view change through `onView` and remember them, and had no way
   *  to hand one back. One method, and the history lives in the studio where the rest of the
   *  command state already is. */
  setView: (v: View) => void;
  /** ⚠ AutoCAD's F, WP and CP selection options. The studio collects the points — they are an
   *  ordinary run of picks — and this answers what they caught, because the display list and
   *  the hit tests live here. */
  selectPoly: (poly: readonly number[], mode: "fence" | "wpoly" | "cpoly") => Pick[];
  /** What the GPU layer's LAST frame did, or `null` when there is no GPU layer.
   *  ⚠ For `qa/frame_cost.py`. A frame cost cannot be reasoned about from the display list —
   *  4,898 fills in a drawing says nothing about how many were drawn or how many triangles the
   *  parity pass rasterised, and both were guessed wrong before they were measured. */
  stats: () => import("./gpu/types").FrameStats | null;
  /** Where a text sits on SCREEN, so the in-place editor can be put on top of it. ⚠ Published
   *  because TEXTEDIT can now be reached from the ribbon as well as by double-clicking, and the
   *  studio has no way to know where a note is in pixels. */
  textBoxOf: (handle: string) => { x: number; y: number; w: number; h: number };
}

/** ⚠ Below this the text is a grey smudge, and drawing it costs more than the whole rest of the
 *  frame — `fillText` is the single most expensive call here. AutoCAD hides small text too. */
const MIN_TEXT_PX = 5;

/** ⚠ A stable empty set as the default prop. `sel = new Set()` in the signature builds a NEW
 *  set on every render, which changes the identity `paint`'s dependency list watches and
 *  repaints the whole drawing on every mouse move. */
const NONE: ReadonlySet<string> = new Set<string>();

/** ⚠⚠ **BELOW THIS A DRAG IS A CLICK.** A drafter's "click" moves the mouse two or three pixels
 *  and AutoCAD forgives that; treating any movement as a selection window means picking a single
 *  object almost never works, which reads as a dead canvas rather than a strict one. */
const DRAG_PX = 3;

/** ⚠ AutoCAD's APERTURE, in SCREEN pixels — the box the snap looks inside. Its default is 10.
 *  Same reasoning as PICK_PX: a tolerance in drawing units stops working at the zoom where
 *  precision matters most. */
let APERTURE_PX = 10;

/** The snaps that name a FIXED point on an object, and so are worth acquiring for tracking.
 *
 * ⚠⚠ **THE OTHER SIX MOVE WITH THE CURSOR.** NEAREST is wherever you are along the line;
 * PERPENDICULAR, TANGENT, EXTENSION, PARALLEL and APPARENT are all computed FROM the cursor.
 * Acquiring one arms an alignment path through a spot that is not a feature of the drawing and
 * that the drafter cannot point at again. */
const ACQUIRABLE: ReadonlySet<string> = new Set([
  "endpoint", "midpoint", "centre", "quadrant", "intersection", "node", "insertion", "geocentre",
]);

export default function Canvas2D({
  dl, dark = true, onView, onApi, onCursor, onSelect, onEditText, onCycle,
  trailing = false, onTrail, onSay,
  sel = NONE, showGrid = true,
  snaps = NONE, onSnap, picking = false, panning = false, onPoint, preview = null,
  fit = null, rubber = null, ghosts = [],
  base = null, rasterUrl,
  grips = null, hotGrip = null, onGrip, sheet = null, uframe = true,
  onRightClick, onZoomExtentsGesture,
  otrack = false, trackAng = 90,
  gpu = false, lwt = false,
}: {
  /** ⚠⚠ **AutoCAD's LWDISPLAY — draw pen weights, or every line as a hairline.** Off is
   *  AutoCAD's default and was this canvas's only behaviour: the status bar has carried the
   *  Show/Hide Lineweight button all along under a tooltip promising to do this, and it
   *  flipped a boolean nothing read.
   *  ⚠ It only decides the WIDTH. Standing the GPU canvas down when weights are on is the
   *  CALLER's job — `glBackend` strokes every instance at one width and has nowhere to put a
   *  second one, so leaving it up would paint hairlines under the weighted strokes and pay for
   *  both. See `Studio`'s `gpu={gpuOn && !tog.lwt}`. */
  lwt?: boolean;
  /** ⚠⚠ **OFF BY DEFAULT, AND IT DRAWS ONLY THE LINEWORK AND THE POINT MARKS.** When on, a
   *  WebGL2/WebGPU canvas sits UNDER this one and paints `polys` + `marks`; the 2D context
   *  skips those two loops and keeps everything else — fills, texts, rays, the grid, the
   *  selection, grips, snaps, the sheet. Measured against the 2D canvas on drawing 1158:
   *  tone +0.5%, mean per-channel 4.03/255. See `studio/gpu/` and `qa/gpu_diff.py`.
   *  ⚠ It exists to be MEASURED, not to be the default: Canvas2D holds 60 fps at the median
   *  and only stutters above ~50k entities (p95 59-72 ms while zooming). */
  gpu?: boolean;
  dl: DisplayList | null;
  /** The PAPER, when a layout is open — see `display.py`'s `_sheet_of`. `null` in model space.
   *
   *  ⚠⚠ **DRAWN, AND NOT JUST FOR LOOKS.** Paper space with no paper is a drawing floating on a
   *  dark field: nothing says where the sheet edge is, so nothing says whether the title block
   *  is on the page or a metre off it, and the drafter is composing a plot blind. AutoCAD draws
   *  the sheet, its shadow and the dashed printable area, and all three carry information. */
  sheet?: Sheet | null;
  /** AutoCAD's `FRAME`. ⚠ Defaults ON, as AutoCAD's does: an underlay you cannot see the edge
   *  of is one you cannot tell is there when the fade is high — which is when you need to. */
  uframe?: boolean;
  dark?: boolean;
  onView?: (v: View) => void;
  onApi?: (api: CanvasApi) => void;
  /** ⚠ Both coordinate systems. The MODEL point is what the status bar and the commands want;
   *  the SCREEN point is where the dynamic-input box has to appear, and deriving it back from
   *  the model point would mean the studio re-implementing this canvas's own view transform. */
  onCursor?: (p: { x: number; y: number } | null, px?: { x: number; y: number }) => void;
  /** ⚠⚠ **A LIST, NOT ONE PICK.** AutoCAD selects many objects and every modify command acts
   *  on the whole set; a canvas that can only report one makes MOVE a one-object command
   *  forever. An EMPTY list means the click landed on nothing, which DESELECTS.
   *  ⚠ `shift` is AutoCAD's REMOVE modifier — see the note on PICKADD in `Studio.tsx`. */
  /** ⚠ `at` is the MODEL point of the click. TRIM needs it — it says which PIECE of the object
   *  to remove — and no other caller looks at it. */
  onSelect?: (picks: Pick[], shift: boolean, at?: { x: number; y: number }) => void;
  /** ⚠⚠ **A DOUBLE-CLICK ON TEXT OPENS THE EDITOR, WHICH IS THE ONLY GESTURE AutoCAD HAS FOR
   *  IT.** There is no ribbon button for "edit this note" — you double-click it, and a drafter
   *  who cannot will conclude the text is not editable at all. Reported with the SCREEN
   *  rectangle as well as the pick, because the editor has to appear ON the text and the studio
   *  does not own this canvas's view transform. */
  onEditText?: (p: Pick, box: { x: number; y: number; w: number; h: number }) => void;
  /** The owner handles currently selected. Owned by the studio, not by this canvas. */
  sel?: ReadonlySet<string>;
  /** ⚠⚠ **SELECTION CYCLING.** A note over a hatch over a wall is three objects at one pixel,
   *  and without a way past the first, two of them cannot be selected at all. A repeated click
   *  at the SAME spot steps to the next one, which is AutoCAD's own gesture. Reported as the
   *  whole candidate list so the studio can say "1 of 3" — a silent cycle looks like a click
   *  that landed somewhere else. */
  onCycle?: (all: Pick[], index: number) => void;
  /** ⚠⚠⚠ **THE COMMAND'S BASE POINT, AND THREE SNAPS ARE MEANINGLESS WITHOUT IT.**
   *  Perpendicular is perpendicular FROM somewhere, a tangent is tangent FROM somewhere, and
   *  parallel is parallel to something THROUGH somewhere. `snap.find` has taken a `from`
   *  argument since it was written and this canvas never passed one — so `perpendicular` sat in
   *  `IMPLEMENTED`, ticked in the drop-up, and was never once offered. Found while adding the
   *  other two, which would have been dead on arrival for exactly the same reason. */
  base?: { x: number; y: number } | null;
  /** Where the pixels of an attached image live. ⚠ A FUNCTION rather than a base URL: the
   *  route is per drawing and behind the same login as the drawing, and the canvas has no
   *  business knowing which drawing it is showing. */
  rasterUrl?: (defHandle: string) => string;
  /** The RUNNING object snaps, by name. ⚠ Empty means OSNAP is off (F3) — see `Studio.tsx`. */
  snaps?: ReadonlySet<string>;
  /** ⚠⚠ **A COMMAND IS ASKING FOR A POINT, SO A CLICK IS NOT A SELECTION.** AutoCAD's whole
   *  modal grammar lives in this one boolean: during MOVE, clicking a wall does not select the
   *  wall, it says "here". A canvas that always selects makes every command that needs two
   *  points impossible. */
  picking?: boolean;
  /** ⚠⚠ **REVCLOUD's `Freehand` — THE ONE TOOL THAT FOLLOWS THE POINTER RATHER THAN COLLECTING
   *  PICKS.** Every other draw tool here reads discrete clicks; a freehand cloud is traced with
   *  the button held down, so the canvas has to report the path as it happens. `onTrail` is
   *  called with each model point while the button is down and with `null` when it comes up —
   *  the null is what tells the studio the stroke is finished, and without it the tool would
   *  wait for an Enter the drafter has no reason to press. */
  trailing?: boolean;
  onTrail?: (p: { x: number; y: number } | null) => void;
  /** ⚠ One line at the command line, for a renderer that is not the one this app prefers. The
   *  canvas cannot reach the transcript; the studio can, and it owns `say`. */
  onSay?: (line: string) => void;
  /** ⚠⚠ **AutoCAD'S `PAN` COMMAND — LEFT-DRAG PANS UNTIL IT IS CANCELLED.** The middle button
   *  pans inside any command and always has; this is the separate thing a drafter gets by
   *  typing `P`, and its own tooltip described it while `defOf` answered "PAN is not built
   *  yet" — the BREAKLINE bug for the third time. See `Studio`'s `mode === "pan"`. */
  panning?: boolean;
  /** Where the click went, in MODEL units and already SNAPPED. */
  onPoint?: (p: { x: number; y: number }) => void;
  /** ⚠⚠ **ZOOM TO EXTENTS WHEN *THIS* CHANGES, NOT WHEN THE DISPLAY LIST DOES.** The display
   *  list is rebuilt by things that are not "a new drawing": a layer toggled off, a MOVE
   *  committed, a REGEN. Re-fitting on any of those throws a drafter out of the detail they are
   *  working on — measured on the first MOVE ever committed here, which zoomed a 1:20 view of
   *  one room out to the whole 115-metre site. Pass the DRAWING's identity. */
  fit?: number | string | null;
  /** Draw the selection through this 2×3 affine, with a rubber band from `from` to `to`.
   *
   *  ⚠⚠ **AN AFFINE, NOT A DISPLACEMENT, BECAUSE ROTATE AND SCALE PREVIEW TOO.** `[a, b, c, d,
   *  e, f]` means `x' = a·x + c·y + e`, `y' = b·x + d·y + f` — the same six numbers SVG and
   *  canvas use. MOVE is `[1, 0, 0, 1, dx, dy]`; making the canvas understand only the last two
   *  would mean rotating 400 objects with no way to see where they land until after the click.
   *  ⚠ Local, so previewing 400 objects costs the server nothing. */
  preview?: { m: [number, number, number, number, number, number];
              from: { x: number; y: number }; to: { x: number; y: number } } | null;
  /** The geometry a DRAW tool is part-way through, with the cursor as its last point.
   *  ⚠ AutoCAD rubber-bands the REAL shape, not a bounding box — a three-point arc that shows a
   *  straight line between the clicks tells a drafter nothing about which way it will bulge, and
   *  that is the only thing they are deciding. */
  /** ⚠ `rect` carries RECTANG's options so the band is the shape that will be COMMITTED. The
   *  band used to be an axis-aligned rectangle between the two points whatever the options said,
   *  so `Dimensions` dragged one size and drew another. See `rectCorners`. */
  rubber?: { kind: string; pts: { x: number; y: number }[];
             rect?: { dims?: { l: number; w: number }; square?: boolean; rot?: number } } | null;
  /** ⚠⚠⚠ **THE SHAPE THAT HAS BEEN POSTED AND NOT YET COME BACK.** Same geometry, same
   *  renderer, one difference: its points are COMPLETE, so the cursor is not appended to them.
   *
   *  It is a SECOND band rather than a state of `rubber`, and LINE is why. LINE re-arms from its
   *  own endpoint the instant a segment is sent (`again` in `Studio`'s point handler), so `draw`
   *  is non-null again in the same tick — a ghost that took `rubber`'s place would never be
   *  drawn for the one command a drafter uses most, which is exactly the case it exists for.
   *  Drawn FIRST so the live band is on top of it. */
  ghosts?: { kind: string; pts: { x: number; y: number }[]; seq: number;
             rect?: { dims?: { l: number; w: number }; square?: boolean; rot?: number } }[];
  /** The selected object's REAL grips, in world coordinates — see `edits.grips_of`. ⚠ Not the
   *  decorative squares drawn from the flattened outline: these are the entity's own geometry
   *  and dragging one changes the document. */
  grips?: { p: [number, number]; role: string }[] | null;
  /** Which grip is HOT. ⚠ AutoCAD turns the picked grip red and the rest stay blue, which is the
   *  only thing on screen that says the next click will land THERE. */
  hotGrip?: number | null;
  onGrip?: (i: number) => void;
  /** What the cursor is snapped to, for the status bar's tag. Fires with null when nothing. */
  onSnap?: (s: SnapHit | null) => void;
  /** The RIGHT button on the drawing — AutoCAD's ENTER. ⚠ The canvas only reports the gesture;
   *  what ENTER means depends on what is running, and only the studio knows that. */
  onRightClick?: () => void;
  /** A double-click of the WHEEL — AutoCAD's ZOOM Extents. ⚠ Reported rather than done here, so
   *  the studio can push the view onto its ZOOM Previous stack first. */
  onZoomExtentsGesture?: () => void;
  /** OTRACK — F11, object snap TRACKING.
   *
   * ⚠⚠ **THE BUTTON EXISTED, F11 TOGGLED IT, AND NOTHING READ IT.** `STATUS_ITEMS` has carried
   * `st.otrack` since the bar was built and `T.otrack` reached this component's caller and
   * stopped there — a lit control that does nothing, which this app's own rules call its worst
   * failure mode. Tracking is where a drafter puts a point that is not ON anything: level with
   * one corner and in line with another. Without it the only route is a construction line they
   * then have to erase.
   * ⚠ Tracking needs OSNAP: the paths run from points that were SNAPPED, so with F3 off there
   * is nothing to acquire and the toggle correctly does nothing. AutoCAD behaves the same. */
  otrack?: boolean;
  /** The angle increment tracking paths run at — POLARANG when polar tracking is on, 90
   *  otherwise. ⚠ AutoCAD's own rule: OTRACK is orthogonal-only until POLAR is on, and then it
   *  follows the same increments, so the two toggles cannot disagree about what a direction is. */
  trackAng?: number;
  showGrid?: boolean;
}) {
  // ⚠⚠ **WHAT A RENDER OF THE CANVAS COSTS, beside `Studio`'s own.** `__pdcadRenderWhy` says a
  // studio render is 50.9 ms when the display list changes and 4-5 ms when it does not, on
  // `ysl-hn-center-2025` — so something in the subtree is O(the drawing) and the signature
  // cannot say which component. Two `performance.now()` a render, read by `/var/tmp/rwhy.py`.
  const _cvT0 = performance.now();
  useLayoutEffect(() => {
    const w = window as unknown as Record<string, unknown>;
    const c = (w.__pdcadCanvasRender ||= { n: 0, ms: 0, max: 0 }) as
      { n: number; ms: number; max: number };
    const d = performance.now() - _cvT0;
    c.n += 1; c.ms += d; if (d > c.max) c.max = d;
  });
  const box = useRef<HTMLDivElement | null>(null);
  const cv = useRef<HTMLCanvasElement | null>(null);
  const view = useRef<View>({ x: 0, y: 0, scale: 1 });
  const size = useRef({ w: 0, h: 0 });
  const cursor = useRef<{ x: number; y: number } | null>(null);
  const drag = useRef<{ px: number; py: number; vx: number; vy: number } | null>(null);
  //: where a left-press went down, and the rubber band it has grown into. ⚠ A REF, not state:
  //: it changes on every mouse move and a `setState` per move re-renders the whole tree.
  //: Where the last pick landed and how far through its candidate list it is. ⚠ Keyed on the
  //: SPOT, not on the object — see `onUp`. Clicking anywhere else must reset it, or the second
  //: click in the drawing would silently select what is behind what was aimed at.
  const cycle = useRef<{ x: number; y: number; i: number; n: number } | null>(null);

  /** Attached rasters, by IMAGEDEF handle. ⚠⚠ **A REF, NOT STATE, AND IT REPAINTS ON LOAD.**
   *  An `Image` arrives asynchronously and the draw pass is synchronous, so the first frame has
   *  nothing to paint; without the `onload` bump the photograph appears only when something
   *  else happens to trigger a redraw, which looks like an image that loads on mouse-move. */
  const rasters = useRef(new Map<string, HTMLImageElement>());
  const [rasterTick, setRasterTick] = useState(0);
  const rasterAt = (def: string): HTMLImageElement | null => {
    if (!rasterUrl) return null;
    const got = rasters.current.get(def);
    if (got) return got;
    const img = new Image();
    // ⚠ The route is same-origin and behind the session cookie; `crossOrigin` would turn a
    // perfectly ordinary fetch into a CORS failure with no message.
    img.onload = () => setRasterTick((n) => n + 1);
    img.onerror = () => setRasterTick((n) => n + 1);   // ⚠ so the FRAME falls back, not nothing
    img.src = rasterUrl(def);
    rasters.current.set(def, img);
    return img;
  };
  const band = useRef<{ x0: number; y0: number; x1: number; y1: number; live: boolean } | null>(
    null);
  //: What the cursor is snapped to right now, and the index it was found in. ⚠ BOTH refs: the
  //: snap changes on every mouse move and the index is megabytes — putting either in state
  //: re-renders the tree sixty times a second.
  const snap = useRef<SnapHit | null>(null);
  /** The grip under the cursor — AutoCAD's grip ROLLOVER, which is not the hot grip.
   *
   * ⚠⚠ **THREE STATES, NOT TWO, AND THE MIDDLE ONE IS THE USEFUL ONE.** A grip is blue when
   * idle, WARM when the cursor is over it, and red once picked. The warm state is the only
   * thing that says "click now and you will get THIS vertex" — without it a drafter aims at a
   * 8 px square and finds out whether they hit it by watching what moves. */
  const hoverGrip = useRef<number | null>(null);
  /** Paints since the page loaded — see `UxProbe.paints`. ⚠ A ref: counting renders with state
   *  would be a render per paint, which is the very thing it exists to detect. */
  const paints = useRef(0);
  /** Points acquired for object snap TRACKING, in model units, oldest first.
   *
   * ⚠ AutoCAD's default is AUTOMATIC acquisition — hovering a snap point during a command
   * acquires it, with no extra keystroke. (The alternative, Shift-to-acquire, is a setting
   * almost nobody changes.) So this fills itself from `snap.current` and needs no dwell timer,
   * which is just as well: a cursor that stops moving stops sending pointermoves, so a dwell
   * would never complete for the drafter holding perfectly still ON the corner they want.
   * ⚠ Capped at AutoCAD's seven. An uncapped list turns every corner hovered on the way across
   * a plan into an alignment path, and the screen fills with dashes. */
  const acquired = useRef<{ x: number; y: number }[]>([]);
  /** How many consecutive moves the cursor has held the SAME snap point.
   *  ⚠ See `ACQUIRABLE` — a drive-by acquisition is what put seven crosses in a column down one
   *  edge in the first screenshot of this feature. */
  const dwell = useRef<{ x: number; y: number; n: number } | null>(null);
  /** The alignment the cursor is currently on, if any — the point it snaps to and the acquired
   *  point (or two) the dashed path is drawn from. */
  const track = useRef<{ pt: { x: number; y: number };
                         from: { x: number; y: number }[] } | null>(null);
  const index = useRef<{ dl: DisplayList; ix: SnapIndex } | null>(null);
  /** Per-poly model-space bounds, built once per display list — see `polyBounds`. */
  const bnds = useRef<{ dl: DisplayList; b: Float64Array; n: number;
                        all: number[] } | null>(null);
  /** The bounds cache, built on demand — `hit()` and the selection walks want it as much as
   *  the paint does, and whichever asks first pays for it once. */
  const boundsFor = (d: DisplayList) =>
    (bnds.current && bnds.current.dl === d
      ? bnds.current
      : (bnds.current = { dl: d, ...polyBounds(d, bnds.current) }));
  /** Per-poly resolved stroke style — see `polyStyles`. */
  const sty = useRef<{ dl: DisplayList; ink: boolean; lwt: boolean; elw: Record<string, number>;
                       ids: Int32Array; defs: PolyStyle[];
                       seen: Map<string, number>; n: number } | null>(null);
  const [, bump] = useState(0);

  //: ⚠⚠⚠ **`toScreen` IS *CANVAS* SPACE, NOT SCREEN SPACE, AND THAT DISTINCTION IS THE WHOLE
  //: DESIGN OF THE ROTATED VIEW.** 22 sites in the paint pass inline this same arithmetic
  //: (`x * v.scale + ox`), and teaching every one of them about a rotation would be 22 chances
  //: to get it wrong in the hottest code in the app. Instead the CONTEXT is rotated once, so
  //: every one of them keeps computing the unrotated coordinate and the canvas maps it. The
  //: only place that has to know is the way BACK — see `toModel`.
  const toScreen = (mx: number, my: number) => ({
    sx: (mx - view.current.x) * view.current.scale + size.current.w / 2,
    sy: size.current.h / 2 - (my - view.current.y) * view.current.scale,
  });
  //: ⚠⚠ **THE INVERSE ROTATION COMES FIRST.** A pointer event is in REAL screen pixels; the
  //: geometry was painted through a rotated context. Un-rotating about the canvas centre —
  //: which is exactly the point the paint pass rotates about — turns it back into the space
  //: `toScreen` produces, and the rest is unchanged. Every pick, snap and hit test goes through
  //: here, so this one function is what makes a rotated view selectable.
  const toModel = (sx: number, sy: number) => {
    const r = view.current.rot || 0;
    let px = sx, py = sy;
    if (r) {
      const cx = size.current.w / 2, cy = size.current.h / 2;
      const dx = sx - cx, dy = sy - cy;
      const c = Math.cos(-r), s2 = Math.sin(-r);
      px = cx + dx * c - dy * s2;
      py = cy + dx * s2 + dy * c;
    }
    return {
      x: (px - size.current.w / 2) / view.current.scale + view.current.x,
      y: view.current.y - (py - size.current.h / 2) / view.current.scale,
    };
  };

  const zoomExtents = useCallback(() => {
    // ⚠⚠ **IN PAPER SPACE, THE EXTENTS ARE THE SHEET.** A real client layout had one stray
    // entity 2.2 metres off a 420 mm page — entirely legal, invisible on the plot, and it made
    // ZOOM EXTENTS fit a 2,205 mm span, so the A3 the drafter clicked the tab for arrived one
    // fifth of the width of the screen in the corner. AutoCAD fits the PAPER when you open a
    // layout, and that is not a convenience: the sheet is the subject of paper space.
    const e = sheet ? [0, 0, sheet.paper[0], sheet.paper[1]] : dl?.extents;
    const { w, h } = size.current;
    if (!e || !w || !h) return;
    const [x0, y0, x1, y1] = e;
    const dx = x1 - x0, dy = y1 - y0;
    // ⚠ A drawing can legitimately be a single point or one horizontal line, and dividing by a
    // zero extent gives Infinity — a blank screen with no error, which reads as a failed load.
    const s = dx > 1e-9 && dy > 1e-9 ? Math.min(w / dx, h / dy) * 0.92
            : dx > 1e-9 ? (w / dx) * 0.92
            : dy > 1e-9 ? (h / dy) * 0.92 : 1;
    view.current = { x: (x0 + x1) / 2, y: (y0 + y1) / 2, scale: s };
    onView?.(view.current);
    bump((n) => n + 1);
  }, [dl, onView, sheet]);

  /** ZOOM Window — fit a model-space box to the canvas. ⚠ Here and not in the studio, because
   *  the CANVAS is the only thing that knows how many pixels it has; a caller computing the
   *  scale would need the size, the device ratio and the rotation, which is the view maths
   *  written twice.
   *  ⚠ FITTED, not stretched: AutoCAD keeps the aspect ratio and shows at least the window you
   *  asked for, so the smaller scale wins. No 0.92 margin — a window IS the request. */
  const fitBox = useCallback((x0: number, y0: number, x1: number, y1: number) => {
    const { w, h } = size.current;
    const dx = Math.abs(x1 - x0), dy = Math.abs(y1 - y0);
    if (!w || !h || dx < 1e-9 || dy < 1e-9) return false;
    view.current = { ...view.current, x: (x0 + x1) / 2, y: (y0 + y1) / 2,
                     scale: Math.min(w / dx, h / dy) };
    onView?.(view.current);
    bump((n) => n + 1);
    return true;
  }, [onView]);

  const zoomTo = useCallback((x: number, y: number, height: number,
                              anchor: "center" | "left" | "right") => {
    const { w, h } = size.current;
    if (!w || !h || !(height > 1e-9)) return false;
    const scale = h / height;
    const halfW = w / scale / 2, halfH = height / 2;
    view.current = {
      ...view.current,
      x: anchor === "left" ? x + halfW : anchor === "right" ? x - halfW : x,
      y: anchor === "center" ? y : y + halfH,
      scale,
    };
    onView?.(view.current);
    bump((n) => n + 1);
    return true;
  }, [onView]);

  const zoomBy = useCallback((f: number) => {
    view.current = { ...view.current, scale: view.current.scale * f };
    onView?.(view.current);
    bump((n) => n + 1);
  }, [onView]);

  /** ⚠ Deliberately does NOT call `onView` — see `Studio.viewPrev`. Announcing a restored view
   *  through the same channel that RECORDS views would push it straight back onto the history,
   *  so pressing Previous twice would bounce between two views forever. */
  const setViewTo = useCallback((v: View) => {
    if (!v || !Number.isFinite(v.scale) || v.scale <= 0) return;
    // ⚠ `rot` is carried through. Dropping it here would make ZOOM PREVIOUS and every restored
    // view silently un-rotate the drawing — a view that comes back at a different angle from
    // the one that was saved.
    view.current = { x: v.x, y: v.y, scale: v.scale,
                     ...(v.rot ? { rot: v.rot } : {}) };
    bump((n) => n + 1);
  }, []);

  // ⚠ `selectPoly` is passed by REF-stable closure rather than added to the dependency list:
  // it reads `dl` through the render closure, and listing it would republish the api on every
  // display-list change — which the studio stores in a ref and would never notice.
  const polyRef = useRef<CanvasApi["selectPoly"]>(() => []);
  polyRef.current = (poly, mode) => hitPoly(poly, mode);
  // ⚠ Same ref trick, same reason: `textBox` reads `dl` and the live view through the render
  // closure, so publishing it directly would republish the api on every pan.
  const boxRef = useRef<CanvasApi["textBoxOf"]>(() => ({ x: 0, y: 0, w: 240, h: 60 }));
  boxRef.current = (h) => textBox(h);
  useEffect(() => { onApi?.({ zoomExtents, zoomBy, fitBox, zoomTo, view: view.current, setView: setViewTo,
                              selectPoly: (p, m) => polyRef.current(p, m),
                              // ⚠ BOTH refs, or the rebuild is a patch: `buildScene` takes the
                              // previous scene and the list it came from, and patches whenever it
                              // can. A REGEN that patched would measure the edit path again.
                              regen: () => { sceneRef.current = null; dlRef.current = null;
                                             setGpuGen((g) => g + 1); },
                              // ⚠ What the GPU layer's LAST frame did, for `qa/frame_cost.py`.
                              // A function, not the object: the backend is rebuilt when the
                              // renderer changes and a captured reference would go stale.
                              stats: () => be.current?.stats ?? null,
                              textBoxOf: (h) => boxRef.current(h) }); },
           [onApi, zoomExtents, zoomBy, fitBox, zoomTo, setViewTo]);

  // ── the GPU layer, when it is on ──────────────────────────────────────────────────────────
  const gcv = useRef<HTMLCanvasElement | null>(null);
  const be = useRef<import("./gpu/types").SceneBackend | null>(null);
  const [gpuGen, setGpuGen] = useState(0);
  //: The last scene and the list it was built from — the two things a patch needs. ⚠ Dropped
  //: whenever the backend is rebuilt, or a patch would be written into a buffer that no
  //: longer exists.
  const sceneRef = useRef<import("./gpu/build").SceneResult | null>(null);
  //: What the GPU was last told to highlight, so panning does not rebuild the ranges. ⚠ Keyed
  //: on the instance count AND on a stamp that changes whenever the `sel` SET changes identity —
  //: its `size` alone would miss a swap of one object for another.
  const hiKey = useRef("");
  const selStamp = useRef(0);
  const lastSel = useRef<ReadonlySet<string> | null>(null);
  const dlRef = useRef<DisplayList | null>(null);

  useEffect(() => {
    if (!gpu) { be.current?.dispose(); be.current = null; return; }
    let dead = false;
    const c = gcv.current;
    if (!c) return;
    // ⚠⚠ **`?backend=webgpu` FORCES ONE, FOR MEASUREMENT.** `qa/gpu_diff.py` could already force
    // a backend on its own harness page; the STUDIO could not, so "is WebGPU faster on a real
    // drawing in the real app" had no way of being asked. ⚠ A forced backend that is unavailable
    // THROWS — forcing one exists to test THAT one, and a quiet fallback would make the answer
    // meaningless. Unforced, nothing changes.
    const forced = new URLSearchParams(location.search).get("backend");
    import("./gpu/select")
      .then(({ createBackend }) =>
        createBackend(c, forced === "webgpu" || forced === "webgl2" ? forced : null))
      .then((b) => {
      // ⚠ A backend built after the effect was torn down must be disposed, not kept: a browser
      // allows only a handful of live GL contexts.
      if (dead) { b.dispose(); return; }
      be.current = b;
      // ⚠ Published so RENDERER can say it — see `Studio.tsx`. The backend knows its own driver
      // string (`WEBGL_debug_renderer_info`) and nothing else in the app ever asked for it.
      (window as unknown as Record<string, unknown>).__pdcadGL =
        { kind: b.kind, renderer: b.renderer };
      setGpuGen((n) => n + 1);
      // ⚠⚠ **AND IT SAYS SO, ONCE, WHERE THE DRAFTER READS EVERYTHING ELSE.** A renderer that
      // quietly became the slower one is the defect this whole line of work started from; the
      // owner found it in a screenshot, which is not a way to run a program. ⚠ Only when
      // something was GIVEN UP — the ordinary case prints nothing, because a message on every
      // load is a message nobody reads.
      const why = (window as unknown as Record<string, unknown>).__pdcadWebGpuWhy;
      if (why) onSay?.(`  drawing with ${b.kind} — WebGPU not used: ${why}`);
    }).catch(() => { /* ⚠ falls back to the 2D linework below — never an exception on screen */ });
    return () => {
      dead = true;
      be.current?.dispose(); be.current = null;
      sceneRef.current = null; dlRef.current = null;   // ⚠ see `sceneRef`
      delete (window as unknown as Record<string, unknown>).__pdcadGL;
    };
  }, [gpu]);

  // ⚠ Rebuilt only when the DRAWING changes. Rebuilding 657,873 instances on every pan frame
  // is CPU work for a buffer whose contents did not move; only the uniform did.
  //
  // ⚠⚠⚠ **`useLayoutEffect`, AND IT IS WORTH A WHOLE SCREEN REFRESH ON EVERY EDIT.** A passive
  // `useEffect` runs AFTER the browser has painted, so the chain was: commit → paint frame N
  // with the OLD picture → effect → build the scene → `requestAnimationFrame` → frame N+1 shows
  // the edit. One frame of every edit was spent painting a picture already known to be stale.
  // A layout effect runs before that paint, and the dynamic `import` resolves in a microtask,
  // which also runs before it — so the scene is ready in time for the frame that is coming
  // anyway. ⚠ Measured against BricsCAD's own instrument, `qa/bench_desktop.py`.
  useLayoutEffect(() => {
    const b = be.current;
    if (!b || !dl) return;
    let cancelled = false;
    // ⚠⚠ **THE GAP AFTER THIS LINE IS NOT THE IMPORT.** The module is cached after the first
    // call, so `.then` is a microtask and cannot run until the current TASK's synchronous work
    // ends — which means `_t1 - _t0` measures the REST OF REACT'S COMMIT, and it has been
    // reported as `__pdcadScene.import` all along. Splitting it needs a mark on the other side
    // of the render, which `Studio.refresh` sets as `__pdcadCommit.set` just before `setDl`.
    // ⇒ `effect - set` is React's render and the layout effects up to this one; `_t1 - effect`
    // is everything after it in the same task.
    {
      const w = window as unknown as Record<string, unknown>;
      const c = (w.__pdcadCommit ?? {}) as Record<string, number>;
      c.effect = performance.now();
      w.__pdcadCommit = c;
    }
    const _t0 = performance.now();
    import("./gpu/build").then(({ buildScene, FILLS_READY, fillStats, patchWhy }) => {
      if (cancelled) return;
      const _t1 = performance.now();
      // ⚠ Carried on a ref rather than imported at the top: `gpu/build` is loaded dynamically
      // so it stays out of the main chunk, and importing one constant from it statically would
      // pull the whole builder back in for every drafter, GPU or not.
      gpuFills.current = FILLS_READY;
      const ink = sheet ? false : dark;
      // ⚠ `uframe` goes DOWN, because the same prop decides `noStroke` in the 2D path above.
      // Two renderers reading one flag from two places is how a frame appears on one and not
      // the other with nothing in either file looking wrong.
      // ⚠⚠ **SPLIT, BECAUSE A SINGLE `build` NUMBER SENDS THE NEXT SESSION TO THE WRONG PASS.**
      // `buildScene` is three: the BAND CUTS (a box over every poly), the LINE instances (15
      // floats each) and the FILL triangulation. On drawing 1158 they add up to 60 ms and this
      // reported one figure, so "the scene rebuild costs 60 ms" was as far as anyone could get.
      // `SceneTimes` was built for this and nothing was passing it.
      const times: SceneTimes = {};
      // ⚠⚠⚠ **THE PREVIOUS SCENE AND THE LIST IT CAME FROM, so a MOVE rewrites the rows that
      // moved instead of all 593,342 instances.** `buildScene` refuses the patch and rebuilds
      // whenever it cannot be certain — a different row count, a colour change, a band cut that
      // shifted — so the worst case is exactly what happened before. See `patchScene`.
      // ⚠ Only when the BACKEND can take a sub-range; otherwise a patched buffer would be
      // uploaded whole anyway and the refusal costs nothing.
      const canPatch = typeof b.patchLines === "function";
      const scene = buildScene(dl, ink, ink ? ACAD_BG : "#ffffff", !!uframe, times,
                               canPatch ? sceneRef.current ?? undefined : undefined,
                               canPatch ? dlRef.current ?? undefined : undefined);
      sceneRef.current = scene;
      dlRef.current = dl;
      const _t2 = performance.now();
      if (scene.patched) {
        b.patchLines!(scene.lines.data, scene.patched.from, scene.patched.to,
                      scene.lines.count, scene.lines.groups, scene.patched.ranges);
        // ⚠ An appended FILL — a dimension's arrowheads, a new hatch. Its triangles went into the
        // buffer's slack, so only that range crosses; the group list comes whole because the band
        // map is rebuilt from it either way.
        if (scene.patched.fills && b.patchFills) {
          b.patchFills(scene.fillData, scene.patched.fills.from, scene.patched.fills.to,
                       scene.fillGroups);
        } else if (scene.patched.fills) {
          b.uploadFills?.(scene.fillData, scene.fillGroups);
        }
      } else {
        b.uploadLines(scene.lines.data, scene.lines.count, scene.lines.dashes,
                      scene.lines.groups);
        b.uploadFills?.(scene.fillData, scene.fillGroups);
      }
      const _t3 = performance.now();
      // ⚠⚠ **WHERE THE POST-EDIT HITCH GOES, PUBLISHED — the display list changing identity
      // rebuilds every instance in the scene, and `qa/edit_time.py` measured 0.45–0.5 s of
      // paint on two frames in five. `import` is the dynamic chunk (0 after the first), `build`
      // is `buildScene` over the WHOLE list, `upload` is `bufferData`. Read by `qa/edit_time.py`.
      (window as unknown as Record<string, unknown>).__pdcadPaintAfter = null;
      (window as unknown as Record<string, unknown>).__pdcadScene = {
        // ⚠ The INSTANT the upload finished, so `__pdcadPaint.at - at` is exactly the wait
        // between a scene being ready and the canvas drawing it — one frame, or none.
        at: _t3,
        import: Math.round(_t1 - _t0), build: Math.round(_t2 - _t1),
        fill: fillStats(),
        cuts: Math.round((times.cuts ?? 0) * 10) / 10,
        bandReuse: times.bandReuse ?? -1, bandFrom: times.bandFrom ?? -1,
        bandNo: times.bandNo ?? "",
        lines: Math.round((times.lines ?? 0) * 10) / 10,
        fills: Math.round((times.fills ?? 0) * 10) / 10,
        patched: scene.patched ? scene.patched.to - scene.patched.from : null,
        // ⚠ how many FLOATS of fill an append wrote, and how much of the buffer is live — the
        // two numbers that say whether a patched scene is carrying every fill a fresh build has.
        fillPatched: scene.patched?.fills
          ? scene.patched.fills.to - scene.patched.fills.from : null,
        fillUsed: scene.fillUsed,
        patchWhy: patchWhy(),
        upload: Math.round(_t3 - _t2), instances: scene.lines.count,
        fillVerts: scene.fillData.length / 2, bands: scene.bands,
      };
      // ⚠⚠ **PAINT NOW, not `schedule()`.** `schedule` asks for a `requestAnimationFrame`, and a
      // rAF registered from inside React's commit does not run until the NEXT frame — so the
      // buffer was uploaded in time for this one and then deliberately not drawn. Painting here
      // is synchronous work inside a frame that was going to paint regardless.
      // ⚠ Any frame already asked for is cancelled first, or the same scene is drawn twice.
      // ⚠⚠ **PAINT NOW, not `schedule()`.** `schedule` asks for a `requestAnimationFrame`, and a
      // rAF registered from inside React's commit does not run until the NEXT frame — so the
      // buffer was uploaded in time for this one and then deliberately not drawn. Painting here
      // is synchronous work inside a frame that was going to paint regardless.
      // ⚠ Any frame already asked for is cancelled first, or the same scene is drawn twice.
      //
      // ⚠⚠⚠ **WORTH 4-9 ms, NOT THE 24 THE FIRST INSTRUMENT CLAIMED.** Three instruments in a
      // row reported about themselves before one could see this at all:
      //   · `qa/bench_desktop.py` types a BURST and divides, so it measures throughput and one
      //     frame of latency is invisible to it — it read 66.1 → 65.5 ms, i.e. nothing.
      //   · `felt` is published from a fixed double `rAF` after the splice, so it cannot move
      //     however early the canvas is drawn.
      //   · polling for `__pdcadPaint` from the harness reads whichever paint happened LAST — a
      //     hover, a cursor blink — and read 24-25 ms on BOTH arms of the A/B.
      // Recording the FIRST paint after each scene, in the page (`__pdcadPaintAfter`), finally
      // separated them: **A 0/9/8/0/4 ms, B 0/0/0/0/0**. Modest, and it is on every draw a
      // drafter makes, which is the case this project's own rule says not to ignore.
      // ⚠ In a `try`: a throw here would leave the buffer uploaded and nothing drawn, which is
      // a blank edit rather than a slow one. The rAF path is the fallback for both failures.
      try {
        const p = paintRef.current;
        if (!p) throw new Error("no paint yet");
        if (frame.current) { cancelAnimationFrame(frame.current); frame.current = 0; }
        p();
      } catch {
        scheduleRef.current?.();
      }
    });
    return () => { cancelled = true; };
    // ⚠ `uframe` IS a dependency: it decides which polys are suppressed in the buffer, so
    // toggling FRAME has to rebuild it. Left out, the 2D path would respond and the GPU one
    // would not — the same control doing two different things depending on the renderer.
  }, [dl, dark, sheet, gpuGen, uframe]);

  /** Whether the GPU builder is emitting fills — see the guard in the fill loop. */
  const gpuFills = useRef(false);
  const paintRef = useRef<(() => void) | null>(null);

  // ── the paint ─────────────────────────────────────────────────────────────────────────────
  const paint = useCallback(() => {
    // ⚠ The NEWEST pending ghost — the input-skip below and the `__pdcadGhost` mark both mean
    // "the object the drafter just drew". `ghosts` is a LIST (see `Studio`): LINE/POINT commit
    // segment by segment and each patch can be seconds on a huge drawing, so every earlier band
    // must stay drawn (the loop further down) or it vanishes while its own patch is in flight.
    const ghost = ghosts.length ? ghosts[ghosts.length - 1] : null;
    //: ⚠⚠ **THE 2D PASS IS THE BIGGER HALF OF THE FRAME AND HAD NO CLOCK ON IT.** The GL
    //: backend reports `cpuMs`/`gpuMs`; this reported nothing, so a frame measured 33.3 ms and
    //: the GL numbers said 5 ms and the missing 28 ms had nowhere to be attributed. Published
    //: on `window.__pdcad2d` for `qa/frame_cost.py`.
    const _t0 = performance.now();
    // ⚠⚠ **SKIP A PREVIEW NOBODY WILL SEE — see `__pdcadInputPending` in `Studio`.** Keys are
    // queued and a command is between two of its own prompts: whatever this frame would draw is
    // replaced before a human could register it, and it costs ~6 ms of canvas a step. Only
    // mid-command, so every command still redraws exactly once. ⚠ RE-SCHEDULED, never dropped:
    // a frame simply discarded would leave the screen stale if the queue emptied without
    // another state change.
    // ⚠⚠⚠ **EXCEPT WHEN THERE IS A GHOST, WHICH IS NOT A PREVIEW — IT IS A COMMITTED OBJECT.**
    // The skip above exists for a band that will be replaced before a human could register it.
    // A `ghost` is the opposite: the command is over, the points are decided, and this is the
    // frame in which the drafter sees what they drew. Suppressing it meant a drafter who TYPES
    // coordinates — `@3450<30`, which is half of how this app is driven — saw nothing at all
    // until the server answered, while one who CLICKED saw the object in 15 ms. Measured on
    // `ysl-hn-center-2025` with `qa/ghost_shot.py`: clicked, the overlay gains the rectangle's
    // 1,127 pixels at 15 ms; typed, `drawBand(ghost)` ran ZERO times and the picture waited out
    // the whole round trip.
    // ⚠ And it is why `qa/bench_actions.py` read a LINE at 150 ms: it types its points, so it
    // measured the path with no feedback and then timed the server on top of it.
    // ⚠⚠ **AND NOT FOR AN OPTIMISTIC TRANSFORM — TRIED, MEASURED, REVERTED.** The reasoning
    // was that a MOVE's local guess is a result too and should not be skipped. It is not on the
    // screen either way: `qa/optimistic_shot.py` photographs the object still drawn WHERE IT WAS
    // at the moment the guess is marked. So letting those frames through bought nothing and cost
    // a full repaint of 1.5 M objects per queued key — one MOVE of one object went from 127 ms
    // to **255**, all of it main thread. Put back when the guess actually paints.
    // ⚠⚠ **AND NOT WHILE AN OPTIMISTIC TRANSFORM IS UNPAINTED — ONE FRAME, CLOSED BELOW.**
    // The GPU buffer already carries the move (`__pdcadScene` is patched ~77 ms after the
    // commit, measured through a held POST) and this skip was the only thing between it and the
    // screen: with keys still queued, a drafter who TYPED `MOVE ⏎ 0,0 ⏎ 300,200 ⏎` watched the
    // object sit still until the server answered, while one who dragged saw it at once.
    // ⚠ The window closes when THIS paint stamps the mark, so the next queued key is a preview
    // again. An earlier attempt left it open until a fixed pair of rAFs in `Studio` stamped it —
    // which fired at 35 ms, before any frame got through, so the window closed having bought
    // nothing and every later key repainted 1.5 M objects: the round trip went 90 ms to 230.
    const wOpt = (window as unknown as
                  { __pdcadOptimistic?: { at?: number; paint?: number | null } | null })
                 .__pdcadOptimistic;
    const optWaiting = !!(wOpt && wOpt.paint == null);
    // ⚠⚠⚠ **AND THE BYPASS FOR AN UNPAINTED TRANSFORM WAS TRIED AND REVERTED — IT COSTS MORE
    // THAN IT BUYS.** Letting the frame through while a guess is unpainted did put the typed
    // MOVE on screen sooner (`opt` 170-199 ms where the round trip is ~250), and it did it by
    // repainting 1.5 million objects against a queue that was still draining: the browser-side
    // round trip went from 90 ms to 216-297 while the server stayed at 26. A drafter typing a
    // second command pays that. ⇒ Left as `!ghost` alone, which is the case that draws one
    // cheap band rather than the whole drawing.
    if ((window as unknown as { __pdcadInputPending?: number }).__pdcadInputPending
        && !ghost) {
      scheduleRef.current?.();
      return;
    }
    // ⚠⚠ **WHEN THE CANVAS WAS ACTUALLY DRAWN — the only honest clock for a FRAME-level change.**
    // `qa/bench_desktop.py` types a BURST and divides, so it measures throughput and cannot see
    // one frame of latency either way; `felt` is published from a fixed double `rAF` after the
    // splice, so it cannot either. Moving the scene build from a passive effect to a layout one
    // is exactly a one-frame change, and neither instrument could tell whether it had worked.
    // ⚠ A COUNTER beside the timestamp: a reader has to know a NEW paint happened, not read the
    // previous one again and call it instant.
    {
      const w = window as unknown as Record<string, unknown>;
      const prev = w.__pdcadPaint as { n?: number } | undefined;
      w.__pdcadPaint = { at: _t0, n: (prev?.n ?? 0) + 1 };
      // ⚠ The GPU layer's own stats, where a harness can reach them. `onApi` hands them to the
      // studio, which is no use to a probe that only has `window` — and the backend's CPU clock
      // is the only honest one for a frame, because an rAF interval can only report a multiple
      // of the vsync. ⚠ The live object, not a copy: it is overwritten in place every frame.
      w.__pdcadGLStats = be.current?.stats ?? null;
      // ⚠⚠ **THE FIRST PAINT AFTER A SCENE, RECORDED HERE RATHER THAN POLLED FOR.** A reader
      // that polls sees whichever paint happened most recently — a hover, a cursor blink, the
      // next pan frame — so an A/B worth one frame read 24 ms on both arms. Cleared where
      // `__pdcadScene` is published; only the first paint after that fills it in.
      if (w.__pdcadPaintAfter == null) w.__pdcadPaintAfter = _t0;
    }
    const c = cv.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;
    const { w, h } = size.current;
    const dpr = window.devicePixelRatio || 1;
    if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) {
      c.width = Math.round(w * dpr); c.height = Math.round(h * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // ⚠⚠ **CLEARED, NOT FILLED, WHEN THE GPU LAYER IS ON.** An opaque paper here covers the
    // canvas underneath completely, and the symptom is a blank drawing with no error at all.
    // The wrapper div already carries the background colour.
    if (gpu && be.current) ctx.clearRect(0, 0, w, h);
    else { ctx.fillStyle = dark ? ACAD_BG : "#ffffff"; ctx.fillRect(0, 0, w, h); }
    if (!dl) return;

    // ⚠ The GPU layer paints in the SAME frame and at the SAME view, so the two can never be a
    // frame apart while panning — which would read as the linework lagging the grips.
    // ⚠ Where the frame's time goes, bisected. `geomMs` covers everything from the top of this
    // callback and was reading **146 ms on a frame that drew NOTHING** — `polysDrawn 0,
    // textsDrawn 0, selMs 0` — while the GL backend's own `cpuMs` was 1. One number over a span
    // that wide cannot say which half, and the two halves have different fixes.
    const _tGpu = performance.now();
    if (gpu && be.current) {
      be.current.resize();
      // ⚠⚠⚠ **THE SELECTION HIGHLIGHT GOES TO THE GPU, WHICH ALREADY HOLDS THE GEOMETRY.** The
      // 2D pass below walked every poly in the display list and emitted every point of the
      // selected ones on EVERY frame: 17.9 ms with one object selected on drawing 10951 and
      // **4,432 ms with everything selected** (`__pdcad2d.selMs`), against ~8 ms for the GPU to
      // draw the whole drawing. See `gpu/build.ts::highlightRanges`.
      // ⚠ Recomputed only when the SELECTION or the SCENE changes — panning must not pay for it.
      if (be.current.setHighlight) {
        // ⚠ `Studio` memoises `selHandles` on `sel`, so a new Set IS a new selection and the
        // same Set is the same one. Identity is the cheap and exact test; `size` is neither
        // (swapping one object for another keeps it).
        if (lastSel.current !== sel) { lastSel.current = sel; selStamp.current += 1; }
        const plan = sceneRef.current?.lines.plan;
        // ⚠ A DRAG is drawn by the 2D pass, which transforms the points — the GPU buffer still
        // holds the geometry where it was, so a highlight left on would sit at the old place
        // while the dashed preview moved.
        if (!plan || !sel.size || !HIGHLIGHT || preview) {
          if (hiKey.current !== "") { be.current.setHighlight(null); hiKey.current = ""; }
        } else {
          const key = `${sceneRef.current!.lines.count}:${sel.size}:${selStamp.current}`;
          if (key !== hiKey.current) {
            hiKey.current = key;
            import("./gpu/build").then(({ highlightRanges }) => {
              const b2 = be.current;
              if (!b2?.setHighlight || hiKey.current !== key) return;
              b2.setHighlight(highlightRanges(plan, sel),
                              dark ? [0.35, 0.66, 1, 1] : [0.04, 0.34, 0.82, 1], 2);
              scheduleRef.current?.();
            });
          }
        }
      }
      be.current.draw({ x: view.current.x, y: view.current.y, scale: view.current.scale,
                        rot: view.current.rot || 0 }, w, h, dpr);
    }
    const _gpuMs = performance.now() - _tGpu;

    const v = view.current;
    const ox = w / 2 - v.x * v.scale;
    const oy = h / 2 + v.y * v.scale;

    // ⚠⚠⚠ **THE ROTATED VIEW, IN ONE PLACE.** AutoCAD's `UCSFOLLOW` swings the drawing so the
    // current UCS's X axis runs across the screen — which is the entire reason a drafter sets
    // a UCS on a wall at 37°: they want to detail that wall as if it were horizontal.
    // ⚠ Rotated about the CANVAS CENTRE, which is the same point `toModel` un-rotates about.
    // The two must agree or every click lands somewhere the drawing is not.
    // ⚠ NEGATIVE, because this canvas's Y runs UP the model and DOWN the screen — the same
    // sign question the UCS icon got wrong for months. Verified in a picture, not reasoned.
    const rot = v.rot || 0;
    if (rot) {
      ctx.save();
      ctx.translate(w / 2, h / 2);
      ctx.rotate(-rot);
      ctx.translate(-w / 2, -h / 2);
    }

    // ⚠ BEFORE the grid, so the grid falls on the paper the way AutoCAD's does.
    if (sheet) drawSheet(ctx, sheet, v, ox, oy, !!(gpu && be.current));
    // ⚠⚠ **COLOUR 7 IS RESOLVED AGAINST THE PAPER, NOT THE SURROUND.** ACI 7 means "the opposite
    // of the background", which on the dark model-space field is white — and a white title block
    // on a white sheet is an invisible title block. AutoCAD flips it in a layout for exactly this
    // reason. Measured on a real A3: the whole plan inside the 1:20 window drew white on white
    // and the sheet read as containing only its coloured layers.
    const ink = sheet ? false : dark;
    if (showGrid) drawGrid(ctx, w, h, v, dark);

    // ⚠⚠ **BATCHING BY COLOUR DESTROYS DRAW ORDER, AND A WIPEOUT IS NOTHING BUT DRAW ORDER.**
    // It hides what is UNDER it and nothing above. Painting every mask before the strokes hides
    // nothing; painting them all after hides the whole drawing. So the stroke pass is split
    // into BANDS at each fill: batch-by-colour inside a band, paint the fill, carry on. A
    // drawing with no fills is one band and one pass — exactly what it was.
    const fills = dl.fills ?? [];
    // ⚠ A wipeout's frame is not drawn (AutoCAD hides it too, `WIPEOUTFRAME` 0) — but its
    // outline stays in `polys` because that is what `hit()` and the snaps read. Skipping the
    // STROKE is what makes it a mask rather than a white box with a line round it.
    const masked = new Set<string>();
    for (const f of fills) if (f[5]) masked.add(f[3]);
    // ⚠ A wide polyline's centreline is in `polys` for PICKING only. Stroking it as well would
    // draw a hairline down the middle of every filled band.
    // ⚠⚠⚠ **BY INDEX, NOT BY OWNER — the owner version made every dimension invisible.**
    // `widened` marks the picking centreline of a wide polyline, whose real outline is in
    // `fills`. Folding it into `masked` suppressed every poly the OWNER had, and a DIMENSION
    // owns five ordinary lines as well as its arrowhead — and `_ARCHTICK`/`_CLOSEDBLANK` ARE
    // wide polylines. Measured: a drawing with one linear dimension drew its defpoint marks and
    // nothing else. See `display.py`'s note; the list is indices as of FORMAT 20.
    const noStroke = new Set<number>(dl.widened ?? []);
    // ⚠ AutoCAD's FRAME variable, off. The frame stays in `polys` — it is the underlay's only
    // PICKING geometry — and only its stroke is suppressed. Hiding the poly instead would make
    // the underlay unselectable, which is a different thing from invisible and not what FRAME 0
    // means.
    if (!uframe) for (const i of dl.uframes ?? []) noStroke.add(i);
    const paper = sheet ? "#ffffff" : (dark ? ACAD_BG : "#ffffff");

    // ⚠ NOT `band` — this file already has a `band` ref, and a local of the same name shadowed
    // it for the rest of the function. TypeScript caught it; a JS codebase would not have.
    const ltypes = dl.ltypes ?? [];
    const elw = dl.elw ?? {};

    // ⚠⚠⚠ **THE VIEW CULL.** A drafter spends the day zoomed into one detail, and until this
    // existed the 2D canvas drew the whole building on every frame of it: drawing 1158 measured
    // 66.6 ms a frame at extents and 49.9 ms zoomed in 30x, where the view holds about a
    // nine-hundredth of the area. 25% cheaper for 900x less to draw is not a renderer that is
    // culling; `qa/frame_cost.py --in` is the instrument that says so.
    // ⚠ The bounds are built ONCE per display list (`polyBounds`) and cached on a ref, because
    // asking each poly for its extent by walking its points every frame is the same work as
    // drawing it.
    const bd = boundsFor(dl);
    const bb = bd.b;
    // ⚠⚠ **THROUGH `toModel`, NOT INLINE ARITHMETIC.** The view can be ROTATED, and this file's
    // whole design for that is that exactly one function knows the way back from canvas space.
    // Re-deriving it here would be the 23rd copy of the forward transform and the first one
    // that had to un-rotate. The four corners' AABB is conservative under rotation, which is
    // the right direction to be wrong in.
    const c0 = toModel(0, 0), c1 = toModel(w, 0), c2 = toModel(0, h), c3 = toModel(w, h);
    // ⚠ A margin, because a poly just off screen still lays down half a stroke width, and pen
    // weights reach 8 px. In model units, so it shrinks as the drafter zooms in.
    const pad = 8 / v.scale;
    const vxi = Math.min(c0.x, c1.x, c2.x, c3.x) - pad;
    const vxa = Math.max(c0.x, c1.x, c2.x, c3.x) + pad;
    const vyi = Math.min(c0.y, c1.y, c2.y, c3.y) - pad;
    const vya = Math.max(c0.y, c1.y, c2.y, c3.y) + pad;
    // ⚠ Not attempted when the whole drawing is on screen. At extents nothing can be culled and
    // the test would be four comparisons on each of 414,943 polys to save nothing at all.
    const cull = bd.all[0] < vxi || bd.all[1] < vyi || bd.all[2] > vxa || bd.all[3] > vya;
    let drew = 0;
    // ⚠ The style table, rebuilt only when something that decides a style moves.
    const st = sty.current && sty.current.dl === dl && sty.current.ink === ink
               && sty.current.lwt === !!lwt && sty.current.elw === elw
      ? sty.current
      : (sty.current = { dl, ink, lwt: !!lwt, elw,
                         // ⚠ Only extendable when the three things that DECIDE a style are
                         // unchanged — otherwise every id in the table means something else.
                         ...polyStyles(dl, ink, !!lwt, elw,
                                       sty.current && sty.current.ink === ink
                                       && sty.current.lwt === !!lwt && sty.current.elw === elw
                                         ? sty.current : null) });
    const sids = st.ids, sdefs = st.defs;
    // ⚠ Both are usually empty, and `Set.has` on 414,943 polys is not free when the answer is
    // always no.
    const anyMask = masked.size > 0, anyNo = noStroke.size > 0;

    const strokeBand = (from: number, to: number) => {
      // ⚠⚠⚠ **THE BAIL-OUT IS FIRST, AND IT USED TO BE LAST.** When the GPU layer has the
      // linework this pass draws nothing — but the check sat AFTER the batching loop, so the 2D
      // canvas still walked all 252,797 polys, resolved every colour, built the whole style map
      // and threw it away, every frame, underneath a renderer that had already drawn them.
      // Measured with `qa/frame_cost.py` on drawing 1158, zoomed 8x: it is the reason the GPU
      // canvas was SLOWER than the 2D one it was supposed to be relieving.
      if (gpu && be.current) return;
      // ⚠⚠ One path per colour AND LINETYPE. See the header — batching is the whole performance
      // story, and `setLineDash` is per-path state, so a dashed run cannot share a path with a
      // solid one. In practice nearly everything is solid: measured on a real client plan,
      // 1,702 dashed runs out of 75,109, across 25 patterns. So this is one extra group per
      // pattern actually used, not one per object.
      // ⚠⚠ **BUCKETED BY A PRECOMPUTED ID, NOT BY A STRING BUILT PER POLY.** See `polyStyles`:
      // a drawing uses a handful of distinct styles and this loop used to rediscover that by
      // allocating one key string and doing one Map lookup for every one of 414,943 polys,
      // every frame. The buckets are a plain array indexed by the id, and `used` keeps the
      // draw order of the buckets deterministic rather than dependent on Map insertion.
      const groups: (number[][] | null)[] = new Array(sdefs.length).fill(null);
      const used: number[] = [];
      const polys = dl.polys, layers = dl.layers;
      for (let k = from; k < to; k++) {
        // ⚠ The cheapest test first and it is four loads: a poly wholly off screen costs
        // nothing beyond this line.
        if (cull) {
          const j = k * 4;
          if (bb[j + 2] < vxi || bb[j] > vxa || bb[j + 3] < vyi || bb[j + 1] > vya) continue;
        }
        const id = sids[k];
        if (id < 0) continue;
        const P = polys[k];
        const ly = layers[P[0] as number];
        if (!ly.on || ly.frozen) continue;         // an off layer does not draw, it is not grey
        if (anyMask && masked.has(P[3] as string)) continue;
        if (anyNo && noStroke.has(k)) continue;
        drew++;
        let arr = groups[id];
        if (!arr) { groups[id] = arr = []; used.push(id); }
        arr.push(P[2] as number[]);
      }
      for (const id of used) {
        const group = groups[id]!;
        const def = sdefs[id];
        const pat = ltypes[def.lt] ?? [];
        ctx.lineWidth = penWidth(def.lw);
        ctx.strokeStyle = def.css;
        // ⚠⚠⚠ **`setLineDash` MEASURES IN SCREEN PIXELS AND THE PATTERN IS IN DRAWING UNITS.**
        // Passing it straight through gives dashes that are correct at scale 1 and invisible or
        // solid at every other zoom — which reads as a linetype that works sometimes.
        // ⚠ And a pattern whose whole cycle is under a pixel is drawn SOLID rather than as grey
        // mush: at that zoom AutoCAD stops dashing too, and a million sub-pixel dash segments
        // is the single most expensive thing this canvas could be asked to do.
        const scaled = pat.length ? pat.map((d) => d * v.scale) : [];
        const cycle = scaled.reduce((a, b) => a + b, 0);
        ctx.setLineDash(cycle >= 1.5 ? scaled : []);
        ctx.beginPath();
        for (const pts of group) {
          ctx.moveTo(pts[0] * v.scale + ox, oy - pts[1] * v.scale);
          for (let i = 2; i < pts.length; i += 2)
            ctx.lineTo(pts[i] * v.scale + ox, oy - pts[i + 1] * v.scale);
        }
        ctx.stroke();
      }
      // ⚠ Left CLEAN. Everything painted after this — fills, the selection, the grips — sets
      // its own dash or expects none, and a stray pattern leaking out of here dashed the
      // wipeouts. ⚠ And the WIDTH for the same reason: with pen weights on this loop leaves
      // `lineWidth` at whatever the last batch used, and the selection highlight drew at 8 px.
      ctx.setLineDash([]);
      ctx.lineWidth = 1;
    };

    ctx.lineWidth = 1;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    // ⚠ Ascending `at`, because the backend appends in entity order but `_compose` adds paper
    // space at the end — sorting is what keeps a sheet's fills from jumping to the front.
    const ordered = [...fills].sort((a, b) => a[6] - b[6]);
    let strokedTo = 0;
    for (const [li, aci, pts, , , mask, at, , grad, rings] of ordered) {
      strokeBand(strokedTo, Math.min(at, dl.polys.length));
      strokedTo = Math.max(strokedTo, Math.min(at, dl.polys.length));
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      // ⚠⚠ **SUPPRESSED WHEN THE GPU LAYER HAS THE FILL, exactly as `strokeBand` is.** Painting
      // it in both places is the same picture at double the cost with no symptom to notice it
      // by — and it would make every p95 measurement of the GPU canvas a measurement of the 2D
      // one doing the same work underneath it.
      // ⚠ A GRADIENT is still drawn HERE: `build.ts` skips those, because a ramp needs a shader
      // and a gradient painted as one flat colour is a picture that lies. The condition has to
      // match that skip or the two renderers disagree about who owns it.
      if (gpu && be.current && gpuFills.current && !grad) continue;
      ctx.beginPath();
      // ⚠⚠ **EVERY SUB-PATH IN ONE PATH, THEN FILLED EVEN-ODD — THAT IS WHAT A HOLE IS.** A
      // hatch bounded by a room and a column arrives as several rings; filling them one at a
      // time paints the column solid. Even-odd needs no winding convention, so it is right
      // whichever direction the client's file stored each loop in — which matters, because
      // these come from twenty years of other people's CAD.
      const ring = (r: readonly number[]) => {
        ctx.moveTo(r[0] * v.scale + ox, oy - r[1] * v.scale);
        for (let i = 2; i < r.length; i += 2)
          ctx.lineTo(r[i] * v.scale + ox, oy - r[i + 1] * v.scale);
        ctx.closePath();
      };
      ring(pts);
      if (rings) for (const r of rings) ring(r);
      // ⚠⚠ **A GRADIENT IS PAINTED ACROSS ITS OWN BOUNDING BOX, WHICH IS WHAT DXF MEANS.**
      // The gradient has no extent of its own in the file — AutoCAD spreads it over the hatch's
      // extents — so the box has to be measured here, per fill, in SCREEN space, or it would
      // not follow a zoom.
      ctx.fillStyle = mask ? paper
        : grad ? gradientStyle(ctx, grad, pts, v.scale, ox, oy,
                               aciToCss(aci, ly.aci, ly.rgb, ink), paper)
               : aciToCss(aci, ly.aci, ly.rgb, ink);
      // ⚠ `evenodd` unconditionally, not only when there are rings: a single ring fills the
      // same either way, and a rule that changes with the data is a rule that gets tested in
      // one shape and shipped in the other.
      ctx.fill("evenodd");
    }
    strokeBand(strokedTo, dl.polys.length);

    // ── construction lines, clipped to the view ─────────────────────────────────────────────
    // ⚠⚠ **AN XLINE IS INFINITE AND THERE IS NOTHING SENSIBLE TO STORE FOR IT.** Its extent is
    // whatever is on screen this frame, so the server sends a base point and a direction and
    // the clipping happens here. A long-but-finite segment baked on the server would end
    // somewhere visible the moment anybody zoomed out — which is a picture of a broken XLINE.
    // ⚠ A RAY runs ONE way. Drawing both halves is the difference between the two entities.
    for (const [li, aci, x, y, dx, dy, half] of dl.rays ?? []) {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      // ⚠ The diagonal of the canvas is always enough to leave the view from any point IN it —
      // but the base point can be far outside, so the reach has to cover that too.
      const px = x * v.scale + ox, py = oy - y * v.scale;
      const reach = Math.hypot(w, h) + Math.hypot(px - w / 2, py - h / 2) + 1;
      ctx.strokeStyle = aciToCss(aci, ly.aci, ly.rgb, ink);
      ctx.beginPath();
      ctx.moveTo(px - (half ? 0 : dx * reach), py + (half ? 0 : dy * reach));
      ctx.lineTo(px + dx * reach, py - dy * reach);
      ctx.stroke();
    }

    // ── POINT entities ──────────────────────────────────────────────────────────────────────
    // ⚠⚠ **SIZED IN PIXELS, NOT IN DRAWING UNITS — WHICH IS AutoCAD'S OWN RULE.** A negative
    // PDSIZE means "this percentage of the viewport", and it exists because a point marker in
    // drawing units is a speck on a site plan and a dinner plate on a detail. Ours is a fixed
    // 3 px cross, which is PDMODE 2 at a constant screen size.
    for (const [li, aci, x, y] of (gpu && be.current ? [] : dl.marks ?? [])) {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      const px = x * v.scale + ox, py = oy - y * v.scale;
      ctx.strokeStyle = aciToCss(aci, ly.aci, ly.rgb, ink);
      ctx.beginPath();
      ctx.moveTo(px - 3, py); ctx.lineTo(px + 3, py);
      ctx.moveTo(px, py - 3); ctx.lineTo(px, py + 3);
      ctx.stroke();
    }

    // ── the selection. ⚠⚠ **DRAWN AFTER EVERYTHING ELSE, ON PURPOSE.** AutoCAD's highlight is
    // ON TOP of the drawing; painting it in the colour pass would let a later entity cover the
    // thing you just picked, and the selection would flicker in and out depending on draw order.
    // ⚠ HIGHLIGHT 0 selects silently — the objects are still selected and still get grips, they
    // are simply not drawn dashed. AutoCAD's own use is a huge selection on a slow machine.
    //: ⚠ The HIGHLIGHT pass's own clock. It walks EVERY poly in the display list — 1,510,939 on
    //: drawing 10951 — and strokes a dashed path for the selected ones, and `geomMs` lumped that
    //: in with the drawing itself. SELECT ALL of 11,458 objects takes seconds and there was no
    //: number that said where they went. Published as `selMs` beside `geomMs`.
    let _selMs = 0;
    if (sel.size && HIGHLIGHT) {
        const _s0 = performance.now();
        ctx.save();
        ctx.strokeStyle = dark ? "#5aa9ff" : "#0b57d0";
        ctx.lineWidth = 2;
        ctx.setLineDash([7, 5]);          // AutoCAD dashes a highlighted object
        ctx.beginPath();
        const gripHints: number[] = [];
        // ⚠ The preview is drawn by TRANSFORMING the same pass, not by a second one. AutoCAD
        // drags the real objects; a ghost outline beside them would not show a drafter whether
        // the corner they are aiming at actually lands on the wall.
        const M = preview?.m;
        const sx1 = (x: number, y: number) => (M ? M[0] * x + M[2] * y + M[4] : x);
        const sy1 = (x: number, y: number) => (M ? M[1] * x + M[3] * y + M[5] : y);
        // ⚠⚠⚠ **SKIPPED ENTIRELY WHEN THE GPU IS DRAWING THE HIGHLIGHT.** This loop walks EVERY
        // poly in the display list — 1,510,939 on drawing 10951 — and emits a `lineTo` per
        // point of the selected ones, and it runs on EVERY FRAME while a selection stands:
        // 17.9 ms with ONE object selected and **4,432 ms with everything selected**. See
        // `SceneBackend.setHighlight`. The text boxes and the grip squares below stay here:
        // there are a few of them, they are not linework, and the GPU buffer has no glyphs.
        // ⚠ The preview transform `M` is NOT applied by the GPU pass, so a selection being
        // DRAGGED still comes through here — the drag has to follow the cursor.
        const gpuHi = !!(gpu && be.current?.setHighlight) && !preview;
        for (const [li, , pts, owner] of (gpuHi ? [] : dl.polys)) {
          if (!sel.has(owner)) continue;
          const ly = dl.layers[li];
          if (ly && (!ly.on || ly.frozen)) continue;
          ctx.moveTo(sx1(pts[0], pts[1]) * v.scale + ox, oy - sy1(pts[0], pts[1]) * v.scale);
          for (let i = 2; i < pts.length; i += 2) {
            ctx.lineTo(sx1(pts[i], pts[i + 1]) * v.scale + ox,
                       oy - sy1(pts[i], pts[i + 1]) * v.scale);
          }
          gripHints.push(pts[0], pts[1], pts[pts.length - 2], pts[pts.length - 1]);
        }
        // ⚠ SELECTED TEXT IS BOXED, not left plain. It is drawn with `fillText` and a dash
        // pattern does not reach a filled glyph, so a selected label looked identical to an
        // unselected one — and a drafter who has just picked a room name has no way to tell.
        for (const [li, , tx, ty, hgt, rot, s, owner, , wid, , ts] of dl.texts) {
          if (!sel.has(owner) || !s) continue;
          const ly = dl.layers[li];
          if (ly && (!ly.on || ly.frozen)) continue;
          // ⚠ Condensed by the same factor the paint uses, or the selection box around a 0.7
          // label is 43% wider than the label — which reads as the wrong thing being selected.
          const wfs = wid > 0 ? 1 : (dl.tstyles?.[ts ?? 0]?.[3] ?? 1);
          const w = s.length * hgt * 0.62 * wfs, h = hgt * 1.25;   // a rough advance is enough
          const cs = Math.cos((rot * Math.PI) / 180), sn = Math.sin((rot * Math.PI) / 180);
          const corner = (u: number, vv: number) => [
            (tx + u * cs - vv * sn) * v.scale + ox, oy - (ty + u * sn + vv * cs) * v.scale];
          const c0 = corner(-hgt * .1, -hgt * .25), c1 = corner(w, -hgt * .25);
          const c2 = corner(w, h - hgt * .25), c3 = corner(-hgt * .1, h - hgt * .25);
          // ⚠ The box follows the preview too — text that stays put while the walls around it
          // rotate reads as text the command is going to leave behind.
          const P = (p: number[]) => [p[0] + (M ? (sx1(tx, ty) - tx) * v.scale : 0),
                                      p[1] - (M ? (sy1(tx, ty) - ty) * v.scale : 0)];
          const q0 = P(c0), q1 = P(c1), q2 = P(c2), q3 = P(c3);
          ctx.moveTo(q0[0], q0[1]); ctx.lineTo(q1[0], q1[1]);
          ctx.lineTo(q2[0], q2[1]); ctx.lineTo(q3[0], q3[1]); ctx.closePath();
          gripHints.push(tx, ty);
        }
        ctx.stroke();
        // ⚠ Decorative squares on the flattened outline, drawn ONLY when the object has no
        // real grips to show — see the `grips` prop. Two sets of squares meaning different
        // things is worse than one set meaning nothing.
        ctx.setLineDash([]);
        ctx.fillStyle = dark ? "#5aa9ff" : "#0b57d0";
        const g = 3;
        for (let i = 0; grips === null && i < gripHints.length && i < 400; i += 2) {
          const gx = sx1(gripHints[i], gripHints[i + 1]) * v.scale + ox;
          const gy = oy - sy1(gripHints[i], gripHints[i + 1]) * v.scale;
          if (gx < -20 || gy < -20 || gx > w + 20 || gy > h + 20) continue;
          ctx.fillRect(Math.round(gx) - g, Math.round(gy) - g, g * 2, g * 2);
        }
        ctx.restore();
        _selMs = performance.now() - _s0;
    }

    // ── the command's rubber band: base point to cursor. ⚠ AutoCAD draws this line and it is
    // what tells a drafter which way and how far, before they commit. Without it a MOVE is a
    // blind drag.
    if (preview) {
      ctx.save();
      ctx.strokeStyle = dark ? "#c8c8c8" : "#333";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(preview.from.x * v.scale + ox, oy - preview.from.y * v.scale);
      ctx.lineTo(preview.to.x * v.scale + ox, oy - preview.to.y * v.scale);
      ctx.stroke();
      ctx.restore();
    }

    // ── the real grips ──────────────────────────────────────────────────────────────────────
    if (grips) {
      ctx.save();
      // ⚠ GRIPSIZE, not a constant — AutoCAD's default is 5 and its dialog is a slider. The
      // drawn square is the variable's own size; the HIT radius below adds the pickbox.
      const s = GRIP_PX;
      grips.forEach((gr, i) => {
        const gx = gr.p[0] * v.scale + ox, gy = oy - gr.p[1] * v.scale;
        if (gx < -20 || gy < -20 || gx > w + 20 || gy > h + 20) return;
        // ⚠ AutoCAD's colours exactly: an unpicked grip is blue, the hot one is red. The only
        // thing on screen that says where the next click will land.
        const hot = i === hotGrip;
        // ⚠ AutoCAD's three: blue idle, WARM under the cursor, red once picked. The hot one
        // wins over the rollover — the cursor is still on it after the click.
        ctx.fillStyle = hot ? "#e8443a"
                      : i === hoverGrip.current ? "#f0a030"
                      : (dark ? "#5aa9ff" : "#0b57d0");
        ctx.strokeStyle = dark ? "#0b1a2a" : "#fff";
        ctx.lineWidth = 1;
        ctx.fillRect(Math.round(gx) - s, Math.round(gy) - s, s * 2, s * 2);
        ctx.strokeRect(Math.round(gx) - s + .5, Math.round(gy) - s + .5, s * 2 - 1, s * 2 - 1);
      });
      ctx.restore();
    }

    // ── the DRAW rubber band ────────────────────────────────────────────────────────────────
    const drawBand = (rb: NonNullable<typeof rubber>, settled: boolean) => {
      const pts = settled ? rb.pts : [...rb.pts, toModel(cursor.current!.x, cursor.current!.y)];
      const rubber = rb;                       // ⚠ the body below reads `rubber` by name
      const S = (p: { x: number; y: number }) =>
        [p.x * v.scale + ox, oy - p.y * v.scale] as const;
      ctx.save();
      ctx.strokeStyle = dark ? "#e8e8e8" : "#222";
      ctx.lineWidth = 1.2;
      ctx.setLineDash([]);
      ctx.beginPath();
      if (rubber.kind === "CIRCLE" && pts.length >= 2) {
        const [c, e] = pts;
        ctx.arc(...S(c), Math.hypot(e.x - c.x, e.y - c.y) * v.scale, 0, Math.PI * 2);
      } else if (rubber.kind === "ELLIPSE" && pts.length >= 3) {
        // ⚠⚠ **AN ELLIPSE, NOT TWO LINE SEGMENTS.** It fell through to the polyline fallback
        // below, so the band a drafter dragged was a bent line and the ghost — which is the
        // same renderer — could not be held afterwards either. Its three points are AutoCAD's:
        // the two ends of one axis and a distance to the other.
        // ⚠ The minor radius is the PERPENDICULAR distance from the third point to the axis, not
        // its distance from the centre; the two differ for every pick that is not square on.
        // ⚠ The rotation is NEGATED, because this canvas's Y runs UP the model and DOWN the
        // screen — the same reason `S` subtracts.
        const [a0, a1, m] = pts;
        const cx = (a0.x + a1.x) / 2, cy = (a0.y + a1.y) / 2;
        const dx = a1.x - a0.x, dy = a1.y - a0.y;
        const len = Math.hypot(dx, dy) || 1;
        const rx = len / 2;
        const ry = Math.abs((m.x - cx) * (-dy / len) + (m.y - cy) * (dx / len));
        const [sx, sy] = S({ x: cx, y: cy });
        ctx.ellipse(sx, sy, rx * v.scale, Math.max(ry, 1e-9) * v.scale,
                    -Math.atan2(dy, dx), 0, Math.PI * 2);
      } else if (rubber.kind === "POINT" && pts.length >= 1) {
        // ⚠ The same fixed 3 px cross `buildLines` writes for a POINT's two marks, so the held
        // shape is the shape that arrives — see the mark branch in `gpu/build.ts`.
        const [qx, qy] = S(pts[0]);
        ctx.moveTo(qx - 3, qy); ctx.lineTo(qx + 3, qy);
        ctx.moveTo(qx, qy - 3); ctx.lineTo(qx, qy + 3);
      } else if (rubber.kind === "RECT" && pts.length >= 2) {
        // ⚠⚠ **THE SAME `rectCorners` THE COMMIT USES.** A rubber band drawn by its own rule is
        // a band that can disagree with the geometry — and it did, for every RECTANG option that
        // derives a side. Four corners rather than `ctx.rect`, because a rotated one is not an
        // axis-aligned box.
        const cs = rectCorners(pts[0], pts[1], rubber.rect || {}).map(S);
        ctx.moveTo(cs[0][0], cs[0][1]);
        for (let i = 1; i < cs.length; i++) ctx.lineTo(cs[i][0], cs[i][1]);
        ctx.closePath();
      } else if (rubber.kind === "ARC" && pts.length >= 3) {
        const cc = circleThrough(pts[0], pts[1], pts[2]);
        if (cc) {
          // ⚠⚠ **THREE POINTS DEFINE ONE CIRCLE AND TWO ARCS, AND THE ANGLES HERE ARE THE
          // CANVAS'S, NOT THE MODEL'S.** A canvas measures angles with y pointing DOWN, so a
          // model angle is its negative — mixing the two drew an arc through the right ends
          // bulging the wrong way, which is exactly the defect the server had at the same time
          // and for a different reason. Both are settled the same way: work out which sweep
          // contains the MIDDLE click, and take that one.
          const c2 = S({ x: cc.x, y: cc.y });
          const sa = (p: { x: number; y: number }) => {
            const q = S(p);
            return Math.atan2(q[1] - c2[1], q[0] - c2[0]);
          };
          const TAU = Math.PI * 2;
          const norm = (t: number) => ((t % TAU) + TAU) % TAU;
          const a0 = sa(pts[0]), a1 = sa(pts[1]), a2 = sa(pts[2]);
          // ⚠ `anticlockwise = false` sweeps from a0 upward to a2, covering `norm(a2 - a0)`.
          const clockwiseHasMid = norm(a1 - a0) <= norm(a2 - a0);
          ctx.arc(c2[0], c2[1], cc.r * v.scale, a0, a2, !clockwiseHasMid);
        } else {
          ctx.moveTo(...S(pts[0]));
          ctx.lineTo(...S(pts[2]));
        }
      } else {
        ctx.moveTo(...S(pts[0]));
        for (let i = 1; i < pts.length; i++) ctx.lineTo(...S(pts[i]));
      }
      ctx.stroke();
      // ⚠ A dot at every point already placed. Without them a polyline five clicks in looks the
      // same as one that lost a click to a mis-aimed snap.
      // ⚠ Not on a SETTLED band: the dots say "you have placed these and I am waiting for the
      // next one", and there is no next one — the command has been sent.
      ctx.fillStyle = dark ? "#ffd24a" : "#a76";
      for (const p of (settled ? [] : rubber.pts)) {
        const q = S(p);
        ctx.fillRect(Math.round(q[0]) - 2, Math.round(q[1]) - 2, 4, 4);
      }
      ctx.restore();
    };
    // ⚠ The ghost first, the live band over it: starting the next segment must never be hidden
    // behind the one still in flight.
    // ⚠⚠⚠ **A MARK IS STAMPED BY THE PAINT THAT DREW IT, NOT BY A FIXED PAIR OF rAFs.** Both
    // `__pdcadGhost` (a drawn object held through the round trip) and `__pdcadOptimistic` (a
    // transform guessed locally) were marked two animation frames after the state changed —
    // right when the paint waits for the next frame, and a whole frame too pessimistic when it
    // does not. This file already records three instruments that could not see a one-frame
    // change for exactly that reason. One rAF after THIS paint is the start of the frame after
    // the one that composites it, whichever path got here.
    // ⚠ Any paint shows the CURRENT display list, so the first one after the mark is by
    // definition the one that shows it.
    const stamp = (key: string) => {
      const w = window as unknown as Record<string, unknown>;
      const m = w[key] as { at?: number; paint?: number | null } | undefined;
      if (!m || m.paint != null || typeof m.at !== "number") return;
      requestAnimationFrame(() => {
        if (w[key] === m && m.paint == null) {
          m.paint = Math.round(performance.now() - (m.at as number));
        }
      });
    };
    // ⚠⚠⚠ **NOT `__pdcadOptimistic` — NOT YET, AND THE DISCREPANCY IS THE POINT.** Stamping
    // the optimistic transform from the paint that drew it reported **165-196 ms** where its
    // own two-rAF mark reports 30-38, on the same commands on the same drawing. One of those is
    // wrong and this file cannot say which: for the GHOST the paint-anchored figure was
    // confirmed by photographing the framebuffer (`qa/ghost_shot.py`), and no such photograph
    // exists for a transform. Forcing an immediate paint for it made the whole action SLOWER —
    // the round trip went 90 ms to 286 — because a synchronous full repaint of 1.5 M objects is
    // not free, so the cheap fix is not available either.
    // ⇒ Left alone until a MOVE can be photographed the way a drawn rectangle was. Publishing
    // either number without that would be picking the one that suits.
    for (const g of ghosts) drawBand(g, true);
    if (ghost) {
      // ⚠⚠⚠ **STAMPED BY THE PAINT THAT DREW IT, NOT BY A FIXED PAIR OF rAFs.** `Studio` used
      // to mark the ghost two animation frames after setting it, which is right when the paint
      // waits for the next frame and a whole frame too pessimistic when it does not — and this
      // file already records three instruments that could not see a one-frame change for
      // exactly that reason. One rAF after THIS paint is the start of the frame after the one
      // that composites it, whichever path got here.
      stamp("__pdcadGhost");
    }
    // ⚠ Closes the one-frame window above, and is the ONLY thing that stamps this mark now —
    // `Studio`'s fixed pair of rAFs fired at 35 ms, before the scene carrying the move was
    // patched at 77, which is why the photograph caught the object still in its old place.
    if (optWaiting) stamp("__pdcadOptimistic");
    if (rubber && cursor.current) drawBand(rubber, false);

    // ── the selection window. ⚠⚠ **THE TWO COLOURS ARE NOT DECORATION — THEY ARE THE MODE.**
    // AutoCAD's drag-right window is BLUE and takes only what is wholly inside; its drag-left
    // crossing is GREEN and takes anything it touches. A drafter reads the colour, not a status
    // message, and gets a different set depending on which way they dragged. Same for the
    // border: the window is solid, the crossing is dashed, so it survives being colour-blind.
    if (band.current?.live) {
      const b = band.current;
      const cross = b.x1 < b.x0;
      const x = Math.min(b.x0, b.x1), y = Math.min(b.y0, b.y1);
      const bw = Math.abs(b.x1 - b.x0), bh = Math.abs(b.y1 - b.y0);
      ctx.save();
      ctx.fillStyle = cross ? "rgba(80, 200, 80, .16)" : "rgba(70, 140, 255, .16)";
      ctx.fillRect(x, y, bw, bh);
      ctx.strokeStyle = cross ? "#5ec85e" : "#5a9aff";
      ctx.lineWidth = 1;
      ctx.setLineDash(cross ? [6, 4] : []);
      ctx.strokeRect(Math.round(x) + .5, Math.round(y) + .5, Math.round(bw), Math.round(bh));
      ctx.restore();
    }

    // ── raster images. ⚠⚠ **UNDER THE GEOMETRY AND OVER NOTHING.** An attached photograph is
    // a backdrop to trace or a detail to annotate; painting it after the lines would hide the
    // drawing it was brought in for. AutoCAD's draw order puts an image at the back by default
    // for the same reason.
    for (const row of dl.images ?? []) {
      const ly = dl.layers[row[0]];
      if (!ly || !ly.on || ly.frozen) continue;
      // ⚠ Destructured rather than indexed: the tuple's last three members are STRINGS, so
      // `row[i]` with a computed `i` is `number | string` and the arithmetic will not compile.
      const [, ax, ay, bx, by, cx2, cy2, dx2, dy2, def, name, , fade, mono, , clip] = row;
      const P = (x: number, y: number) => ({ x: x * v.scale + ox, y: oy - y * v.scale });
      const a = P(ax, ay), b = P(bx, by), c = P(cx2, cy2), d = P(dx2, dy2);
      const img = def ? rasterAt(def) : null;
      if (img && img.complete && img.naturalWidth) {
        ctx.save();
        // ⚠⚠ **A CLIP MASKS; IT DOES NOT RESIZE.** The whole page is drawn behind a mask, so
        // what shows is a CROP at the page's own scale. Fitting the page to the clip rectangle
        // instead squashes it — every traced dimension then wrong, and the picture still looks
        // like a plan. ⚠ Inside the same save/restore as the transform, and set BEFORE it: a
        // clip path is in the coordinates current when it is declared.
        if (clip && clip.length >= 6) {
          ctx.beginPath();
          for (let i = 0; i < clip.length; i += 2) {
            const q = P(clip[i], clip[i + 1]);
            if (i === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y);
          }
          ctx.closePath();
          ctx.clip();
        }
        // ⚠⚠ **FADE, AND WITHOUT IT AN UNDERLAY BURIES THE DRAWING IT WAS BROUGHT IN FOR.**
        // White geometry over a white scanned page is unreadable, which is precisely the
        // situation AutoCAD's fade exists for. The value is the entity's own — a client's 60%
        // arrives looking the way they left it.
        if (fade) ctx.globalAlpha = Math.max(0.05, 1 - fade / 100);
        // ⚠⚠ **MONOCHROME IS WHAT MAKES A WALL LINE TRACEABLE AT 35% FADE**, which is why
        // AutoCAD puts it under Adjust beside the fade rather than in a colour dialog. The
        // contrast lift is part of it: desaturating alone gives grey mush at that alpha.
        if (mono) ctx.filter = "grayscale(1) contrast(1.6)";
        // ⚠⚠⚠ **THE ORIGIN IS THE TOP-LEFT CORNER `d`, NOT THE INSERT POINT — AND GETTING THAT
        // WRONG DRAWS THE PHOTOGRAPH UPSIDE DOWN.** A DXF `IMAGE`'s insert is its LOWER-left
        // corner and `v_pixel` runs UP the page, while a raster's first row is its TOP. So the
        // image's own X axis is `d→c` and its Y axis is `d→a`, which runs down. Seen in a
        // screenshot before it was seen in the code: the attached picture rendered with its
        // caption inverted, which no count could have caught.
        // ⚠⚠⚠ **`transform`, NOT `setTransform` — AND THE DIFFERENCE COST THE ROTATED VIEW ITS
        // UNDERLAY.** `setTransform` REPLACES the whole matrix, so this threw away both the
        // device-pixel-ratio scale set at the top of the paint AND the UCSFOLLOW rotation: with
        // the view turned 35°, every line, every note and the sheet outline swung and the
        // traced PDF stayed sitting square. Seen in a picture; nothing else could have shown it.
        // ⚠ And the DPR half was already wrong before any of this — on a HiDPI screen every
        // attached image and underlay was drawn at 1/dpr of its size. It never showed here
        // because the screenshot box runs at dpr 1.
        ctx.transform((c.x - d.x) / img.naturalWidth, (c.y - d.y) / img.naturalWidth,
                      (a.x - d.x) / img.naturalHeight, (a.y - d.y) / img.naturalHeight,
                      d.x, d.y);
        try { ctx.drawImage(img, 0, 0); } catch { /* a broken raster is a frame, below */ }
        ctx.restore();
      } else if (name) {
        // ⚠⚠ **THE NAME IN THE FRAME, WHICH IS THE WHOLE POINT WHEN THE FILE IS MISSING.** An
        // anonymous empty rectangle reads as a defect in pdcad; the same rectangle saying
        // `P1010802.JPG` reads as what it is — a picture on a machine this drawing left behind.
        const cx = (a.x + c.x) / 2, cy = (a.y + c.y) / 2;
        const wide = Math.hypot(b.x - a.x, b.y - a.y);
        if (wide > 60) {
          ctx.save();
          ctx.fillStyle = dark ? "rgba(255,255,255,.30)" : "rgba(0,0,0,.35)";
          ctx.font = `${Math.max(9, Math.min(14, wide / 14)).toFixed(1)}px system-ui`;
          ctx.textAlign = "center";
          ctx.fillText(name, cx, cy);
          ctx.textAlign = "left";
          ctx.restore();
        }
      }
    }

    // ── unresolved underlays. ⚠⚠ **A MARKER AND A NAME, NEVER A GUESSED FRAME.** 602 of this
    // office's PDF underlays have no boundary path, so their extent is a page size that lives
    // in a file on somebody's dead D: drive. A rectangle drawn at an invented size would be a
    // confident wrong answer; a cross with `SHU-1702…pdf p1` beside it is a true one, and it is
    // what AutoCAD shows for an underlay it cannot resolve.
    for (const row of dl.underlays ?? []) {
      const ly = dl.layers[row[0]];
      if (!ly || !ly.on || ly.frozen) continue;
      const [, ux, uy, uname] = row;
      const sx = ux * v.scale + ox, sy = oy - uy * v.scale;
      if (sx < -200 || sy < -200 || sx > w + 200 || sy > h + 200) continue;
      ctx.save();
      // ⚠ SCREEN space, like the snap markers: an underlay reference has no size in the
      // drawing, so a marker that scaled with the zoom would be a dot or fill the sheet.
      ctx.strokeStyle = dark ? "rgba(255,190,90,.75)" : "rgba(170,110,0,.8)";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(sx - 9, sy - 9, 18, 18);
      ctx.beginPath();
      ctx.moveTo(sx - 9, sy - 9); ctx.lineTo(sx + 9, sy + 9);
      ctx.moveTo(sx + 9, sy - 9); ctx.lineTo(sx - 9, sy + 9);
      ctx.stroke();
      if (uname) {
        ctx.fillStyle = dark ? "rgba(255,190,90,.85)" : "rgba(170,110,0,.9)";
        ctx.font = "11px system-ui";
        ctx.fillText(uname, sx + 14, sy + 4);
      }
      ctx.restore();
    }

    // ── text. ⚠ Drawn with the browser's own fonts — see `display.py`; a glyph outline is
    // enormous and shipping one per character would dwarf the geometry.
    ctx.textBaseline = "alphabetic";
    // ⚠⚠ **A MASK DRAWN AFTER A NOTE HAS TO HIDE THE NOTE**, and text is painted in its own
    // pass long after the fills. Covering a label is what most wipeouts in this office's
    // drawings are for, so a mask that took out the geometry and left the words on top read as
    // a broken wipeout. A text is hidden when a LATER mask (`ta > i`) contains its insertion
    // point.
    // ⚠ Whole text or none — this canvas has no clip-per-glyph, and half a hidden label would
    // be worse than either. Said here rather than discovered.
    const masks = (dl.fills ?? []).filter((f) => f[5]);
    const covers = (poly: number[], px: number, py: number) => {
      let inside = false;
      for (let i = 0, j = poly.length - 2; i < poly.length; j = i, i += 2) {
        const xi = poly[i], yi = poly[i + 1], xj = poly[j], yj = poly[j + 1];
        if ((yi > py) !== (yj > py)
            && px < ((xj - xi) * (py - yi)) / (yj - yi || 1e-12) + xi) inside = !inside;
      }
      return inside;
    };
    const _tt0 = performance.now();
    let _drawn = 0;
    let ti = -1;
    for (const [li, aci, x, y, hgt, rot, s, , , wid, att, ts, ax, ay] of dl.texts) {
      ti++;
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen || !s) continue;
      // ⚠⚠⚠ **THE CHEAP TESTS FIRST — THE WIPEOUT TEST IS NOT CHEAP AND IT USED TO RUN FIRST.**
      // `covers` is a point-in-polygon ray cast over EVERY VERTEX of a mask, and it ran for
      // every label against every mask before the two constant-time culls below had a chance to
      // reject the label outright. Drawing 46 is 2,998 labels and 246 wipeouts: measured, the
      // text loop cost **25.60 ms of a 31.70 ms 2D pass — to draw TWO labels.** Everything else
      // was rejected, and 2,996 of them were paying the full mask sweep to be rejected.
      // ⚠ These are independent `continue` guards, so the order changes the COST and nothing
      // else; the same labels are drawn either way.
      const px = hgt * v.scale;
      if (px < MIN_TEXT_PX) continue;
      const sx = x * v.scale + ox, sy = oy - y * v.scale;
      if (sx < -400 || sy < -400 || sx > w + 400 || sy > h + 400) continue;
      if (masks.some((m) => m[7] > ti && covers(m[2], x, y))) continue;
      const st = dl.tstyles?.[ts ?? 0];
      const wf = st ? st[3] : 1;
      ctx.save();
      ctx.translate(sx, sy);
      if (rot) ctx.rotate((-rot * Math.PI) / 180);
      // ⚠⚠ **THE OBLIQUE IS A SHEAR, NOT AN ITALIC.** DXF group 51 leans the strokes of the
      // font the drawing already names; asking the browser for the italic CUT of that family
      // substitutes a different typeface. Every style measured here is 0.0, so this costs
      // nothing on this office's work and is right when a file does use it.
      if (st && st[4]) ctx.transform(1, 0, -Math.tan((st[4] * Math.PI) / 180), 1, 0, 0);
      // ⚠⚠⚠ **THE WIDTH FACTOR IS A HORIZONTAL SCALE AND THERE IS NO FONT PROPERTY FOR IT.**
      // `ctx.fontStretch` takes CSS keywords ("condensed"), not the 0.65 and 0.7 a drawing
      // actually stores, and picking the nearest keyword lands on whatever condensed cut the
      // machine happens to have rather than on 0.7 of the real one. A transform is exact.
      // ⚠ Applied AFTER the rotation, so it condenses along the text's own baseline — before
      // it, a note at 30° would be squashed against the world's X axis instead.
      if (wf !== 1) ctx.scale(wf, 1);
      ctx.fillStyle = aciToCss(aci, ly.aci, ly.rgb, ink);
      ctx.font = st
        ? `${st[2] ? "italic " : ""}${st[1] !== 400 ? `${st[1]} ` : ""}${px.toFixed(1)}px ${st[0]}`
        : `${px.toFixed(1)}px "Arial", "Liberation Sans", sans-serif`;
      // ⚠⚠ **AN MTEXT IS THE MULTI-LINE OBJECT — DRAWING IT ON ONE LINE IS NOT A COSMETIC
      // SHORTFALL.** A three-line room note came back as one long run straight out through the
      // wall it labelled, and the drawing still passed every count. `width < 0` is the flag for
      // an old-style TEXT, which has no box and no wrapping and is drawn exactly as before.
      _drawn++;
      // ⚠⚠⚠ **A TEXT'S OWN JUSTIFICATION, AND IT IS NOT MTEXT'S.** `width < 0` marks an
      // old-style TEXT, and that path drew every one of them left-aligned on the baseline —
      // 56.9% of this office's text says otherwise. DXF group 72 is horizontal (0 left, 1
      // centre, 2 right, 3 aligned, 4 middle, 5 fit) and 73 vertical (0 baseline, 1 bottom,
      // 2 middle, 3 top); `display.py` packs them as `halign * 4 + valign + 1`, with 0 kept for
      // the default so an older cache still reads correctly.
      // ⚠ halign 4 is MIDDLE — centred BOTH ways, and it overrides valign rather than combining
      // with it. Treating it as "centre horizontally, baseline vertically" puts a tag half a
      // character-height off, which looks like a font problem and is not.
      // ⚠ ALIGNED (3) and FIT (5) also centre: `display.py` sends the midpoint of the two ends.
      // Neither scales the glyphs to fit yet, and neither occurs in this corpus.
      if (wid < 0) {
        // ⚠⚠⚠ **ALIGNED AND FIT ARE THE ONLY JUSTIFICATIONS THIS CANVAS HAS TO SOLVE RATHER THAN
        // LOOK UP, AND IT IS THE ONLY THING THAT CAN.** Both span two points, and how much to
        // squeeze the words depends on how wide they MEASURE in the font actually loaded — which
        // the server cannot know. So `display.py` sends both ends and the arithmetic is here.
        // ⚠ ALIGNED scales BOTH ways: the height changes with the width, so the letters keep
        // their shape and the run fills the gap. FIT keeps the height and condenses only the
        // width — that is the whole difference between the two, and drawing either one for both
        // gives text that is the right length and the wrong size.
        // ⚠ The rotation is the LINE's, not the entity's: an aligned label runs from the first
        // point to the second whatever `rotation` says, and AutoCAD ignores group 50 for these.
        if (TWO_POINT.has(halignOf(att)) && ax !== undefined && ay !== undefined) {
          const dx = (ax - x) * v.scale, dy = -(ay - y) * v.scale;
          const span = Math.hypot(dx, dy);
          const w0 = ctx.measureText(s).width * (wf !== 1 ? wf : 1);
          if (span > 0.5 && w0 > 0.01) {
            ctx.restore();                       // drop the entity's own rotation and factor
            ctx.save();
            ctx.translate(sx, sy);
            ctx.rotate(Math.atan2(dy, dx));
            ctx.fillStyle = aciToCss(aci, ly.aci, ly.rgb, ink);
            const k = span / w0;
            // ⚠ FIT's vertical scale is 1: the height is what the drafter set and only the
            // spacing gives. ALIGNED takes the same factor both ways.
            ctx.scale(k, halignOf(att) === 3 ? k : 1);
            if (st && st[4]) ctx.transform(1, 0, -Math.tan((st[4] * Math.PI) / 180), 1, 0, 0);
            if (wf !== 1) ctx.scale(wf, 1);
            ctx.font = st
              ? `${st[2] ? "italic " : ""}${st[1] !== 400 ? `${st[1]} ` : ""}`
                + `${px.toFixed(1)}px ${st[0]}`
              : `${px.toFixed(1)}px "Arial", "Liberation Sans", sans-serif`;
            ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
            ctx.fillText(s, 0, 0);
            ctx.restore(); continue;
          }
        }
        if (att > 0) {
          const an = anchorOf(att);
          ctx.textAlign = an.align;
          ctx.textBaseline = an.baseline;
        }
        ctx.fillText(s, 0, 0);
        // ⚠ Reset BOTH: the context is restored below, but `textAlign`/`textBaseline` are set on
        // a saved context here and left set for the wipeout and grip passes otherwise.
        ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
        ctx.restore(); continue;
      }
      // ⚠⚠ **THE LIMIT IS DIVIDED BY THE WIDTH FACTOR.** `measureText` reports the font's own
      // advance and knows nothing about the transform above it, so a 0.7-condensed note wrapped
      // as though it were full width — breaking lines 43% early and leaving a ragged box the
      // drafter never drew.
      const lines = wrapMText(ctx, s, wid > 0 ? (wid * v.scale) / wf : 0);
      // ⚠ 1.667, not 1.0. AutoCAD's "single" MTEXT line spacing is 5/3 of the character height;
      // stacking lines one char-height apart makes ascenders touch descenders.
      const step = px * 1.667;
      const tall = (lines.length - 1) * step + px;
      const vert = Math.floor((Math.max(1, att) - 1) / 3);   // 0 top · 1 middle · 2 bottom
      const horz = (Math.max(1, att) - 1) % 3;               // 0 left · 1 centre · 2 right
      ctx.textAlign = horz === 0 ? "left" : horz === 1 ? "center" : "right";
      // ⚠ The insert is the corner of the BOX, so the first BASELINE is a character-height
      // below it — not on it. Anchoring the baseline at the corner floats the whole note above
      // the box the drafter dragged, by exactly one line.
      let base = px + (vert === 1 ? -tall / 2 : vert === 2 ? -tall : 0);
      for (const line of lines) { ctx.fillText(line, 0, base); base += step; }
      ctx.textAlign = "left";
      ctx.restore();
    }

    const _textMs = performance.now() - _tt0;

    // ⚠⚠⚠ **THE GEOMETRY PASS ENDS HERE AND THE OVERLAYS ARE NOT PART OF IT.** The crosshair
    // is positioned from a POINTER EVENT — real screen pixels — and the snap marker is a glyph
    // that has to stay readable. Leaving either inside the rotated context puts the crosshair
    // where the drawing was rather than where the pointer is, which is the same class of defect
    // as a click that lands 2.5 m from the cursor.
    if (rot) ctx.restore();
    //: ⚠ `geomMs` is the GEOMETRY pass only — it stops here, before the crosshair and the snap
    //: marker, which are positioned from a pointer event and are not part of the drawing. A
    //: getter that read `performance.now()` when the HARNESS asked would have timed everything
    //: after the frame as well, which is not a measurement of anything.
    (window as unknown as { __pdcad2d?: unknown }).__pdcad2d = {
      textMs: _textMs, textsDrawn: _drawn, texts: dl.texts.length,
      //: ⚠ `polysDrawn` against `polys` is the cull, published so it can be MEASURED rather
      //: than believed. A cull that silently stopped working would show here as the two
      //: numbers becoming equal while the frame time went back up.
      polysDrawn: drew, polys: dl.polys.length, culling: cull,
      geomMs: performance.now() - _t0,
      //: ⚠ `gpuMs` is `resize` + `setHighlight` + `draw` as the MAIN THREAD sees it, which is not
      //: what the backend's own `cpuMs` reports: GL calls return before the driver has done
      //: them, and the stall lands on whatever touches the context next. `head` is everything
      //: before that — entering the callback, sizing the canvas, clearing it.
      gpuMs: Math.round(_gpuMs * 10) / 10,
      head: Math.round((_tGpu - _t0) * 10) / 10,
      //: ⚠ INSIDE `geomMs`, not beside it — the highlight is drawn in the same pass.
      selMs: Math.round(_selMs * 10) / 10, sel: sel.size,
    };
    //: A canvas coordinate as the pointer would see it. ⚠ The FORWARD rotation — `toModel` does
    //: the inverse, and the two must be each other's mirror or the snap marker sits away from
    //: the point it is marking.
    const real = (cx: number, cy: number) => {
      if (!rot) return [cx, cy] as const;
      const dx = cx - w / 2, dy = cy - h / 2;
      const c2 = Math.cos(-rot), s3 = Math.sin(-rot);
      return [w / 2 + dx * c2 - dy * s3, h / 2 + dx * s3 + dy * c2] as const;
    };
    if (cursor.current) {
      // ⚠ The crosshair TURNS WITH THE UCS — AutoCAD's does, and it is the cue that tells a
      // drafter which way "horizontal" now means. Rotated about the CURSOR, not the canvas
      // centre: it must stay under the pointer.
      if (rot) {
        ctx.save();
        ctx.translate(cursor.current.x, cursor.current.y);
        ctx.rotate(-rot);
        ctx.translate(-cursor.current.x, -cursor.current.y);
      }
      drawCrosshair(ctx, w, h, cursor.current, dark);
      if (rot) ctx.restore();
    }
    // ── object snap tracking. ⚠⚠ **THE DASHED PATH IS THE ENTIRE FEEDBACK.** Tracking that
    // silently moves the point is indistinguishable from a cursor that has stuck; the line back
    // to the corner it is aligned with is what makes it legible, and it is why AutoCAD draws
    // one. Under the crosshair and the marker, over the drawing.
    if (track.current) {
      ctx.save();
      ctx.strokeStyle = dark ? "#8a8a5a" : "#9a9a3a";
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 4]);
      const tp = real(track.current.pt.x * v.scale + ox, oy - track.current.pt.y * v.scale);
      for (const a of track.current.from) {
        const ap = real(a.x * v.scale + ox, oy - a.y * v.scale);
        // ⚠ Extended PAST the cursor, not stopped at it — AutoCAD's path runs off the screen,
        // which is what says "this is a direction" rather than "this is a segment".
        const dx = tp[0] - ap[0], dy = tp[1] - ap[1];
        const len = Math.hypot(dx, dy) || 1;
        const far = Math.max(w, h) * 1.5;
        ctx.beginPath();
        ctx.moveTo(ap[0] - (dx / len) * far, ap[1] - (dy / len) * far);
        ctx.lineTo(ap[0] + (dx / len) * far, ap[1] + (dy / len) * far);
        ctx.stroke();
      }
      ctx.restore();
    }
    // ⚠ The acquired points carry a small cross — AutoCAD's mark for "this one is armed". Drawn
    // whether or not a path is live, because that is the only thing that says WHY a path
    // appeared when the cursor happens to line up.
    if (acquired.current.length) {
      ctx.save();
      ctx.strokeStyle = dark ? "#8a8a5a" : "#9a9a3a";
      ctx.lineWidth = 1.2;
      for (const a of acquired.current) {
        const p2 = real(a.x * v.scale + ox, oy - a.y * v.scale);
        ctx.beginPath();
        ctx.moveTo(p2[0] - 4, p2[1]); ctx.lineTo(p2[0] + 4, p2[1]);
        ctx.moveTo(p2[0], p2[1] - 4); ctx.lineTo(p2[0], p2[1] + 4);
        ctx.stroke();
      }
      ctx.restore();
    }
    // ⚠ AFTER the crosshair, and under nothing. The marker is the one thing on screen that says
    // "the click will land HERE, not where the pointer is".
    if (snap.current) {
      drawSnapMarker(ctx, ...real(snap.current.x * v.scale + ox,
                                  oy - snap.current.y * v.scale),
                     snap.current.kind);
    }
    // ⚠ NO UCS icon here — `AcadUcsIcon` already draws one on the rail, and its own comment
    // explains the zero-width flex trick that lands it at the drawing's bottom-left corner.
    // Drawing a second would put two origins on screen four pixels apart.

    // ── the UX probe. ⚠ Read by `qa/ux_survey.py` and by nothing in the app; see `uxprobe.ts`.
    // Published from INSIDE the paint because that is the only place a LIVE selection band
    // exists — the colour and the dash that tell a drafter window from crossing are drawn and
    // discarded within one frame, and no screenshot can be taken in the middle of a drag.
    paints.current += 1;
    publishUX({
      paints: paints.current,
      view: { x: v.x, y: v.y, scale: v.scale, rot: v.rot },
      cursorModel: cursor.current ? toModel(cursor.current.x, cursor.current.y) : null,
      band: band.current?.live
        ? (band.current.x1 < band.current.x0
             ? { mode: "crossing", color: "#5ec85e", dash: true }
             : { mode: "window", color: "#5a9aff", dash: false })
        : null,
      grips: grips?.length ?? 0,
      gripPt: grips?.length
        ? { x: grips[0].p[0] * v.scale + ox, y: oy - grips[0].p[1] * v.scale } : null,
      hotGrip: hotGrip ?? -1,
      hoverGrip: hoverGrip.current ?? -1,
      snap: snap.current
        ? { kind: snap.current.kind, x: snap.current.x, y: snap.current.y } : null,
    });
  }, [dl, dark, showGrid, sel, snaps, preview, rubber, ghosts, grips, hotGrip, rasterTick, gpu, gpuGen,
      otrack]);

  // ⚠ The GPU upload effect above needs a repaint once its buffer lands, and it runs BEFORE
  // this callback exists on the first pass. A ref, refreshed every render, is the only way it
  // can call the CURRENT paint rather than one captured when the layer mounted.
  paintRef.current = paint;

  /** Paint on the next animation frame, once, however many times it is asked for.
   *
   * ⚠ **COALESCING, AND THAT IS ALL IT IS.** `useEffect(() => paint())` runs after EVERY render,
   * so several state changes in one tick meant several full repaints of a 252,797-object
   * drawing. This collapses them into one frame, which is the standard shape for a canvas and
   * is worth having on its own.
   *
   * ⚠⚠ **IT DOES NOT FIX THE GPU CANVAS NOT COMPOSITING, AND IT WAS WRITTEN HOPING IT WOULD.**
   * Drawing WebGL outside an animation frame is a real hazard and was the fifth hypothesis for
   * why that layer is blank in the studio; moving the draw inside one changed nothing, with or
   * without a following input event. Said here so the next reader does not take this comment as
   * the fix and stop looking. See `docs/GAP.md`'s twenty-first pass for the one fact that
   * localises it: **nudging the canvas one pixel — `style.left = "1px"` — makes the whole
   * drawing appear at once**, so the content is there and the compositor is never told to
   * re-take that layer.
   *
   * ⚠ Both canvases still paint in the SAME callback, so they cannot be a frame apart while
   * panning — which would read as the linework lagging the grips.
   */
  const frame = useRef(0);
  const schedule = useCallback(() => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      paintRef.current?.();
    });
  }, []);
  const scheduleRef = useRef(schedule);
  scheduleRef.current = schedule;
  useEffect(() => () => { if (frame.current) cancelAnimationFrame(frame.current); }, []);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      size.current = { w: Math.max(1, r.width), h: Math.max(1, r.height) };
      scheduleRef.current();
    });
    ro.observe(el);
    const r = el.getBoundingClientRect();
    size.current = { w: Math.max(1, r.width), h: Math.max(1, r.height) };
    return () => ro.disconnect();
  }, [paint]);

  // ⚠⚠ **THROUGH A REF, AND THE REF IS THE POINT.** Keying the effect on `[fit, zoomExtents]`
  // looks right and does nothing: `zoomExtents` is a `useCallback` over `dl`, so it takes a new
  // identity every time the display list is rebuilt and re-fires the effect anyway. The first
  // attempt at this fix changed the dependency list, rebuilt, and the view still jumped to
  // extents on every MOVE — the screenshot is what said so.
  const fitRef = useRef(zoomExtents);
  fitRef.current = zoomExtents;
  useEffect(() => { fitRef.current(); }, [fit]);
  useEffect(() => { schedule(); });

  //: ⚠⚠⚠ **A GHOST IS PAINTED NOW, NOT NEXT FRAME — IT IS THE ANSWER TO A CLICK.** `schedule`
  //: asks for a `requestAnimationFrame`, and one registered from inside React's commit does not
  //: run until the NEXT frame: the drafter's line was ready and deliberately not drawn for
  //: 16.7 ms. The same reasoning, and the same cancel-first rule, as the scene upload above.
  //: Measured on ysl-hn-center-2025: every ghosted draw sat at exactly 32 ms — two frames —
  //: except PLINE, which happened to land in a frame that was already painting and read 17.
  //: ⚠ Only on the EDGE from no ghost to a ghost. Painting on every render that has one would
  //: draw the whole canvas again for a cursor move.
  //: ⚠ The same for an OPTIMISTIC TRANSFORM, which is the other thing this app puts on screen
  //: before the server has agreed to it. `Studio.commit` marks it and changes `dl`; without this
  //: the first paint that showed the objects in their new place arrived with the server's own
  //: delta — 165-189 ms, measured — and the two-rAF mark it used to carry reported 30-38 by
  //: timing the state change rather than the picture.
  // ⚠ Force an immediate paint whenever a NEW ghost is ADDED (its seq is higher than the last
  // we painted), not only on the edge from none to one — LINE adds one per segment and each must
  // appear within the frame. A cursor move changes no seq, so it does not trigger a repaint.
  const lastGhostSeq = useRef(0);
  useEffect(() => {
    const top = ghosts.length ? ghosts[ghosts.length - 1].seq : 0;
    if (top > lastGhostSeq.current) {
      if (frame.current) { cancelAnimationFrame(frame.current); frame.current = 0; }
      paintRef.current?.();
    }
    lastGhostSeq.current = top;
  });

  // ── the gestures. ⚠⚠ AutoCAD's, not a web map's — see each note ──────────────────────────
  const onWheel = (e: React.WheelEvent) => {
    // ⚠ Zoom about the CURSOR, not the centre. AutoCAD's ZOOM keeps the point under the pointer
    // still, which is what lets a drafter dive into a detail without re-panning after every step.
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const sx = e.clientX - r.left, sy = e.clientY - r.top;
    const before = toModel(sx, sy);
    // ⚠ ZOOMFACTOR 60 reproduces the 1.15 this canvas always used, so a drafter who never
    // types the variable sees no change. AutoCAD's own curve is steeper at the top of the range.
    const step = 1 + ZOOM_FACTOR / 400;
    const f = e.deltaY < 0 ? step : 1 / step;
    view.current.scale *= f;
    const after = toModel(sx, sy);
    view.current.x += before.x - after.x;
    view.current.y += before.y - after.y;
    onView?.(view.current);
    bump((n) => n + 1);
  };

  /** What is under the cursor. ⚠ Nearest, not first — two entities overlap constantly in a real
   *  drawing and "the first one in the list" is an arbitrary answer that changes when the file
   *  is re-imported. */
  const hit = (sx: number, sy: number): Pick | null => {
    if (!dl) return null;
    const v = view.current;
    const m = toModel(sx, sy);
    const tol = PICK_PX / v.scale;             // the pickbox, converted to drawing units
    const tol2 = tol * tol;
    let best: Pick | null = null;
    let bestD = Infinity;
    // ⚠⚠⚠ **THIS RUNS ON EVERY MOUSE MOVE, AND IT USED TO WALK EVERY POINT IN THE DRAWING.**
    // On drawing 1158 that is 414,943 polys and 1,202,368 segment-distance computations for one
    // pointer event — on the main thread, between two frames a drafter is watching. The same
    // per-poly bounds the paint pass culls with reject all but a handful in four comparisons,
    // and the pickbox is a MODEL-space box around the cursor.
    const bb = boundsFor(dl).b;
    const polys = dl.polys;
    for (let k = 0; k < polys.length; k++) {
      const j = k * 4;
      if (bb[j + 2] < m.x - tol || bb[j] > m.x + tol) continue;
      if (bb[j + 3] < m.y - tol || bb[j + 1] > m.y + tol) continue;
      const [li, aci, pts, owner, kind] = polys[k];
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;   // you cannot pick what is not drawn
      for (let i = 0; i + 3 < pts.length; i += 2) {
        const d = seg2(m.x, m.y, pts[i], pts[i + 1], pts[i + 2], pts[i + 3]);
        if (d < tol2 && d < bestD) {
          bestD = d;
          best = { handle: owner, kind, layer: ly.name, aci };
        }
      }
    }
    // ⚠⚠ **A POINT AND A CONSTRUCTION LINE ARE NOT IN `polys`, SO NEITHER WAS PICKABLE.** They
    // draw from their own channels — see `marks` and `rays` — and a hit test that reads only
    // `polys` gives an entity you can see, cannot click, and therefore cannot erase. Both are
    // tested here at the same pickbox as everything else.
    for (const [li, aci, x, y, owner, kind] of dl.marks ?? []) {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      const d = (m.x - x) * (m.x - x) + (m.y - y) * (m.y - y);
      if (d < tol2 && d < bestD) { bestD = d; best = { handle: owner, kind, layer: ly.name, aci }; }
    }
    for (const [li, aci, x, y, dx, dy, half, owner, kind] of dl.rays ?? []) {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      // ⚠ The perpendicular distance to an INFINITE line — and for a RAY only forward of its
      // base point, because a ray is a half line and the other half is not there to click.
      const t = (m.x - x) * dx + (m.y - y) * dy;
      if (half && t < 0) continue;
      const px = x + dx * t, py = y + dy * t;
      const d = (m.x - px) * (m.x - px) + (m.y - py) * (m.y - py);
      if (d < tol2 && d < bestD) { bestD = d; best = { handle: owner, kind, layer: ly.name, aci }; }
    }
    return best;
  };

  /** Everything a rectangle catches. ⚠⚠ **WINDOW AND CROSSING ARE DIFFERENT QUESTIONS ASKED OF
   *  THE SAME BOX, AND AGGREGATED PER OWNER.** A window takes an entity only if EVERY primitive
   *  it decomposes to is wholly inside — so a title block half off-screen is not caught, which
   *  is the whole point of dragging right. A crossing takes it if anything touches. Asking the
   *  question per primitive instead would let a window grab the two lines of a block that happen
   *  to be inside and call that the block. */
  const hitRect = (ax: number, ay: number, bx: number, by: number,
                   crossing: boolean): Pick[] => {
    if (!dl) return [];
    const p0 = toModel(Math.min(ax, bx), Math.max(ay, by));      // screen y is flipped
    const p1 = toModel(Math.max(ax, bx), Math.min(ay, by));
    const [lx, ly_, hx, hy] = [p0.x, p0.y, p1.x, p1.y];
    const acc = new Map<string, { pick: Pick; all: boolean; any: boolean }>();
    const see = (owner: string, pick: Pick) => {
      let a = acc.get(owner);
      if (!a) acc.set(owner, (a = { pick, all: true, any: false }));
      return a;
    };
    const inside = (x: number, y: number) => x >= lx && x <= hx && y >= ly_ && y <= hy;
    const selBb = boundsFor(dl).b;
    const selPolys = dl.polys;
    for (let k = 0; k < selPolys.length; k++) {
      const [li, aci, pts, owner, kind] = selPolys[k];
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      const a = see(owner, { handle: owner, kind, layer: ly.name, aci });
      // ⚠⚠ **THE ACCUMULATOR IS STILL TOUCHED, AND THAT IS THE WHOLE CARE HERE.** A poly wholly
      // outside the box cannot have a vertex inside it and cannot cross it, so both point loops
      // are skipped — but `all` MUST still go false, because an entity is only WINDOW-selected
      // when every part of it is inside and this poly is a part that is not. Skipping the row
      // outright would leave `all` true and window-select an entity hanging half out of the box.
      const j = k * 4;
      if (selBb[j + 2] < lx || selBb[j] > hx || selBb[j + 3] < ly_ || selBb[j + 1] > hy) {
        a.all = false;
        continue;
      }
      for (let i = 0; i + 1 < pts.length; i += 2) {
        if (inside(pts[i], pts[i + 1])) a.any = true;
        else a.all = false;
      }
      // ⚠ A segment can cross the box with BOTH ends outside it — a long wall passing through a
      // small crossing window. Testing vertices alone misses exactly the case a crossing exists
      // for, and the miss looks like a flaky selection rather than a missing test.
      if (crossing && !a.any) {
        for (let i = 0; i + 3 < pts.length; i += 2) {
          if (segRect(pts[i], pts[i + 1], pts[i + 2], pts[i + 3], lx, ly_, hx, hy)) {
            a.any = true;
            break;
          }
        }
      }
    }
    for (const [li, aci, tx, ty, , , , owner, kind] of dl.texts) {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      const a = see(owner, { handle: owner, kind, layer: ly.name, aci });
      if (inside(tx, ty)) a.any = true;
      else a.all = false;
    }
    // ⚠ A POINT is one vertex, so window and crossing ask the same question of it.
    for (const [li, aci, x, y, owner, kind] of dl.marks ?? []) {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      const a = see(owner, { handle: owner, kind, layer: ly.name, aci });
      if (inside(x, y)) a.any = true;
      else a.all = false;
    }
    // ⚠⚠ **AN INFINITE LINE IS NEVER WHOLLY INSIDE A WINDOW AND THAT IS NOT A BUG.** AutoCAD's
    // window select cannot catch an XLINE either — you need a crossing, because "entirely
    // within the box" is false for something with no ends. So `all` stays false always, and a
    // crossing catches it when the line passes through the box.
    for (const [li, aci, x, y, dx, dy, half, owner, kind] of dl.rays ?? []) {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      const a = see(owner, { handle: owner, kind, layer: ly.name, aci });
      a.all = false;
      if (!crossing || a.any) continue;
      // A long-enough chord in each direction to cross any box on screen.
      const reach = (Math.abs(hx - lx) + Math.abs(hy - ly_) + 1) * 4
                  + Math.hypot(x - lx, y - ly_);
      const x0 = half ? x : x - dx * reach, y0 = half ? y : y - dy * reach;
      if (segRect(x0, y0, x + dx * reach, y + dy * reach, lx, ly_, hx, hy)) a.any = true;
    }
    const out: Pick[] = [];
    for (const a of acc.values()) if (crossing ? a.any : a.all && a.any) out.push(a.pick);
    return out;
  };

  /** Everything a FENCE, WPOLYGON or CPOLYGON catches. Model coordinates, flat `[x, y, ...]`.
   *
   * ⚠⚠ **THREE MODES, ONE WALK, AND THEY ASK THREE DIFFERENT QUESTIONS.** A fence catches what
   * it CROSSES and nothing it merely encloses — drag a fence through the middle of a room and
   * you get the four walls, not the furniture inside. A window polygon catches only what is
   * WHOLLY inside. A crossing polygon catches anything that touches. Collapsing them into "does
   * it overlap" would give three buttons with one behaviour, which is worse than having one.
   *
   * ⚠ Aggregated per OWNER, exactly as `hitRect` does: a window that took the two lines of a
   * title block that happen to be inside, and called that the block, is the bug that note exists
   * for.
   */
  const hitPoly = (poly: readonly number[], mode: "fence" | "wpoly" | "cpoly"): Pick[] => {
    if (!dl || poly.length < 4) return [];
    const acc = new Map<string, { pick: Pick; all: boolean; any: boolean }>();
    const see = (owner: string, pick: Pick) => {
      let a = acc.get(owner);
      if (!a) acc.set(owner, (a = { pick, all: true, any: false }));
      return a;
    };
    const closed = mode !== "fence";
    const edges = (fn: (x0: number, y0: number, x1: number, y1: number) => void) => {
      for (let i = 0; i + 3 < poly.length; i += 2) fn(poly[i], poly[i + 1], poly[i + 2], poly[i + 3]);
      // ⚠ A polygon's LAST edge runs back to its first point and a fence's does not. Closing a
      // fence would catch everything on the straight line home, which the drafter never drew.
      if (closed && poly.length >= 6) {
        fn(poly[poly.length - 2], poly[poly.length - 1], poly[0], poly[1]);
      }
    };
    const eat = (li: number, aci: number, pts: readonly number[], owner: string, kind: string) => {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) return;
      const a = see(owner, { handle: owner, kind, layer: ly.name, aci });
      if (mode === "wpoly" || mode === "cpoly") {
        for (let i = 0; i + 1 < pts.length; i += 2) {
          if (inPoly(pts[i], pts[i + 1], poly)) a.any = true;
          else a.all = false;
        }
      }
      if (a.any && mode !== "wpoly") return;
      // ⚠ A segment can cross the boundary with BOTH ends outside it, which is the whole point
      // of a fence and the commonest case for a crossing polygon over a long wall.
      for (let i = 0; i + 3 < pts.length && !(a.any && mode !== "wpoly"); i += 2) {
        edges((x0, y0, x1, y1) => {
          if (segCross(pts[i], pts[i + 1], pts[i + 2], pts[i + 3], x0, y0, x1, y1)) {
            a.any = true;
            // ⚠ Crossing the boundary means it is NOT wholly inside, whatever its vertices say.
            a.all = false;
          }
        });
      }
    };
    for (const [li, aci, pts, owner, kind] of dl.polys) eat(li, aci, pts, owner, kind);
    for (const [li, aci, tx, ty, , , , owner, kind] of dl.texts) eat(li, aci, [tx, ty], owner, kind);
    for (const [li, aci, x, y, owner, kind] of dl.marks ?? []) eat(li, aci, [x, y], owner, kind);
    const out: Pick[] = [];
    for (const a of acc.values()) {
      if (mode === "wpoly" ? a.all && a.any : a.any) out.push(a.pick);
    }
    return out;
  };

  /** EVERY entity under the pickbox, nearest first — what selection cycling steps through.
   *
   * ⚠⚠ **`hit()` RETURNS ONE ANSWER AND A REAL DRAWING STACKS THINGS.** A note over a hatch over
   * a wall is three objects at one pixel, and without a way to reach past the first, two of them
   * cannot be selected at all. AutoCAD cycles with a repeated click at the same spot.
   * ⚠ Text is included by its BOX — see `hitText` for why the strokes are not enough. */
  const hitAll = (sx: number, sy: number): Pick[] => {
    if (!dl) return [];
    const m = toModel(sx, sy);
    const tol = PICK_PX / view.current.scale;
    const tol2 = tol * tol;
    const best = new Map<string, { pick: Pick; d: number }>();
    const allBb = boundsFor(dl).b;
    const allPolys = dl.polys;
    for (let k = 0; k < allPolys.length; k++) {
      const j = k * 4;
      if (allBb[j + 2] < m.x - tol || allBb[j] > m.x + tol) continue;
      if (allBb[j + 3] < m.y - tol || allBb[j + 1] > m.y + tol) continue;
      const [li, aci, pts, owner, kind] = allPolys[k];
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      for (let i = 0; i + 3 < pts.length; i += 2) {
        const d = seg2(m.x, m.y, pts[i], pts[i + 1], pts[i + 2], pts[i + 3]);
        if (d >= tol2) continue;
        const was = best.get(owner);
        if (!was || d < was.d) best.set(owner, { pick: { handle: owner, kind, layer: ly.name, aci }, d });
      }
    }
    const t = hitText(sx, sy);
    // ⚠ A text-box hit sorts LAST among equals — clicking a letter's own stroke should still
    // pick the text first, and clicking inside its box over a wall should offer both.
    if (t && !best.has(t.handle)) best.set(t.handle, { pick: t, d: tol2 });
    for (const [li, aci, x, y, owner, kind] of dl.marks ?? []) {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen || best.has(owner)) continue;
      const d = (m.x - x) * (m.x - x) + (m.y - y) * (m.y - y);
      if (d < tol2) best.set(owner, { pick: { handle: owner, kind, layer: ly.name, aci }, d });
    }
    return [...best.values()].sort((a, b) => a.d - b.d).map((v) => v.pick);
  };

  /** The text row under a screen point. ⚠ Its own test because a text's BOX is what a drafter
   *  aims at — the strokes in `polys` have gaps between the letters, and a double-click that
   *  lands in one of them opens nothing and reads as "text cannot be edited". */
  const hitText = (sx: number, sy: number): Pick | null => {
    if (!dl) return null;
    const m = toModel(sx, sy);
    const tol = PICK_PX / view.current.scale;
    for (const [li, aci, tx, ty, hgt, rot, s, owner, kind, wid, atp, ts] of dl.texts) {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      const lines = s.split("\n");
      // ⚠ A rough box: `hgt` per line, and a width from the MTEXT box where there is one and
      // from the longest line otherwise. It only has to be good enough to aim at.
      // ⚠⚠ **CONDENSED BY THE SAME FACTOR THE PAINT USES**, or a 0.7 label's box is 43% wider
      // than the letters and double-clicking the empty air beside a note opens it. An MTEXT's
      // `wid` is a real box width in drawing units and is already correct.
      const wfh = wid > 0 ? 1 : (dl.tstyles?.[ts ?? 0]?.[3] ?? 1);
      const w = wid > 0 ? wid : hgt * 0.62 * wfh * Math.max(1, ...lines.map((L) => L.length));
      const h = hgt * lines.length * 1.2;
      // ⚠ Undo the text's own rotation before testing, or a note at 90° has a box lying flat.
      const a = (-rot * Math.PI) / 180;
      const dx = m.x - tx, dy = m.y - ty;
      const lx = dx * Math.cos(a) - dy * Math.sin(a);
      const ly2 = dx * Math.sin(a) + dy * Math.cos(a);
      // ⚠ `wid < 0` is a single-line TEXT sitting on its BASELINE; an MTEXT's point is the
      // corner its `attachment` names, and 1–3 are the TOP row, which grows downwards.
      const top = wid < 0 ? hgt : (atp <= 3 ? 0 : atp <= 6 ? h / 2 : h);
      if (lx >= -tol && lx <= w + tol && ly2 <= top + tol && ly2 >= top - h - tol) {
        return { handle: owner, kind, layer: ly.name, aci };
      }
    }
    return null;
  };

  /** Where a text sits on SCREEN, so the editor can be put on top of it. */
  const textBox = (handle: string) => {
    const row = dl?.texts.find((t) => t[7] === handle);
    const v = view.current;
    if (!row) return { x: 0, y: 0, w: 240, h: 60 };
    const [, , tx, ty, hgt, , s, , , wid, atp, ts] = row;
    const lines = s.split("\n");
    const wfh = wid > 0 ? 1 : (dl?.tstyles?.[ts ?? 0]?.[3] ?? 1);
    const w = wid > 0 ? wid : hgt * 0.62 * wfh * Math.max(8, ...lines.map((L) => L.length));
    const h = hgt * lines.length * 1.2;
    const top = wid < 0 ? hgt : (atp <= 3 ? 0 : atp <= 6 ? h / 2 : h);
    const p = toScreen(tx, ty - top);
    return { x: p.sx, y: p.sy, w: Math.max(60, w * v.scale), h: Math.max(20, h * v.scale) };
  };

  /** The grip under a screen point, or -1. ⚠ A slightly wider box than the pickbox: a grip is
   *  a target a drafter aims AT, and missing it selects something else instead. */
  const nearestGrip = (sx: number, sy: number): number => {
    if (!grips) return -1;
    // ⚠ The grip's own size plus the pickbox — a grip is easier to hit than a line, which is
    // what makes grip editing feel direct. Both are the drafter's own variables now.
    let best = -1, bestD = (PICK_PX + GRIP_PX) * (PICK_PX + GRIP_PX);
    grips.forEach((gr, i) => {
      const { sx: gx, sy: gy } = toScreen(gr.p[0], gr.p[1]);
      const d = (gx - sx) * (gx - sx) + (gy - sy) * (gy - sy);
      if (d <= bestD) { bestD = d; best = i; }
    });
    return best;
  };

  /** Where an acquired point's alignment paths put the cursor, or null.
   *
   * ⚠⚠ **THE INTERSECTION OF TWO PATHS BEATS EITHER ONE**, and it is the reason tracking is
   * worth having: level with the top of one wall and in line with the side of another is a
   * point that exists on no object, and AutoCAD places it in one click. Checked first, because
   * a cursor near a crossing is also near both of the lines that cross.
   * ⚠ The paths run BOTH WAYS from the acquired point. AutoCAD's do — a corner is as useful to
   * track leftwards from as rightwards.
   */
  const trackAt = (m: { x: number; y: number }): typeof track.current => {
    const step = trackAng > 0 ? trackAng : 90;
    const tol = APERTURE_PX / view.current.scale;
    // Every ray from every acquired point, as a point and a unit direction.
    const rays: { a: { x: number; y: number }; dx: number; dy: number }[] = [];
    for (const a of acquired.current) {
      for (let deg = 0; deg < 180; deg += step) {
        const th = (deg * Math.PI) / 180;
        rays.push({ a, dx: Math.cos(th), dy: Math.sin(th) });
      }
    }
    // ⚠ The perpendicular distance to an INFINITE line, which is what "both ways" means.
    const near = rays.filter((r) =>
      Math.abs((m.x - r.a.x) * r.dy - (m.y - r.a.y) * r.dx) <= tol);
    // Two rays from DIFFERENT acquired points that cross near the cursor.
    for (let i = 0; i < near.length; i++) {
      for (let j = i + 1; j < near.length; j++) {
        const p = near[i], q = near[j];
        if (p.a === q.a) continue;
        const den = p.dx * q.dy - p.dy * q.dx;
        if (Math.abs(den) < 1e-9) continue;          // parallel: no crossing
        const t = ((q.a.x - p.a.x) * q.dy - (q.a.y - p.a.y) * q.dx) / den;
        const pt = { x: p.a.x + p.dx * t, y: p.a.y + p.dy * t };
        if (Math.hypot(pt.x - m.x, pt.y - m.y) <= tol * 1.5) {
          return { pt, from: [p.a, q.a] };
        }
      }
    }
    // Otherwise the closest single path, with the cursor projected onto it.
    let best: typeof track.current = null;
    let bestD = Infinity;
    for (const r of near) {
      const d = Math.abs((m.x - r.a.x) * r.dy - (m.y - r.a.y) * r.dx);
      if (d >= bestD) continue;
      const t = (m.x - r.a.x) * r.dx + (m.y - r.a.y) * r.dy;
      bestD = d;
      best = { pt: { x: r.a.x + r.dx * t, y: r.a.y + r.dy * t }, from: [r.a] };
    }
    return best;
  };

  const onDown = (e: React.PointerEvent) => {
    // ⚠ MIDDLE button pans. That is AutoCAD's wheel-drag, and a drafter's hand already knows it;
    // left-drag has to stay free for the selection window.
    // ⚠ `panning` joins the middle button rather than replacing it: PAN being active must not
    // take the wheel-drag away, or a drafter who reaches for the habit finds it gone.
    if (e.button === 1 || (e.button === 0 && e.altKey) || (panning && e.button === 0)) {
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      drag.current = { px: e.clientX, py: e.clientY, vx: view.current.x, vy: view.current.y };
      return;
    }
    if (e.button !== 0) return;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const sx = e.clientX - r.left, sy = e.clientY - r.top;
    if (picking) {
      // ⚠ The SNAPPED point when there is one. A command that ignores the marker a drafter just
      // lined up is worse than one with no snap at all — they trusted it.
      // ⚠ Then the TRACKED point: an alignment path is also a marker they lined up, and it is
      // only consulted when no real snap was caught — see the ordering note in `onMove`.
      onPoint?.(snap.current ?? track.current?.pt ?? toModel(sx, sy));
      return;
    }
    // ⚠ Captured so a window dragged off the edge of the canvas still ENDS. Without it the
    // pointerup lands on the ribbon and the rubber band stays on screen forever.
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    band.current = { x0: sx, y0: sy, x1: sx, y1: sy, live: false };
  };
  const onMove = (e: React.PointerEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const sx = e.clientX - r.left, sy = e.clientY - r.top;
    cursor.current = { x: sx, y: sy };
    // ⚠ BEFORE the snap work: a freehand stroke wants every sample it can get, and the snap
    // engine has nothing to offer a hand-drawn line anyway. ⚠ `buttons`, not `button`: on a move
    // event `button` is meaningless and `buttons & 1` is the left one still being held.
    if (trailing && (e.buttons & 1)) onTrail?.(toModel(sx, sy));
    if (drag.current) {
      const d = drag.current;
      view.current.x = d.vx - (e.clientX - d.px) / view.current.scale;
      view.current.y = d.vy + (e.clientY - d.py) / view.current.scale;
      onView?.(view.current);
    }
    // ⚠ The snap is computed BEFORE the coordinate readout, because the readout must show the
    // SNAPPED point. AutoCAD's does, and it is how a drafter confirms they caught the corner
    // rather than a spot 2 mm off it.
    if (snaps.size && !drag.current) {
      const m = toModel(sx, sy);
      const ix = snapIndex();
      const got = ix ? ix.find(m.x, m.y, APERTURE_PX / view.current.scale, snaps, base) : null;
      if ((got?.kind ?? null) !== (snap.current?.kind ?? null)
          || got?.x !== snap.current?.x || got?.y !== snap.current?.y) {
        snap.current = got;
        onSnap?.(got);
      }
    } else if (snap.current) {
      snap.current = null;
      onSnap?.(null);
    }
    // ── object snap TRACKING. ⚠ After the snap, because a real snap beats an alignment: a point
    // that is ON the corner is a better answer than one merely level with it.
    if (otrack && picking && !drag.current) {
      if (snap.current) {
        const a = acquired.current;
        const sp = snap.current;
        // ⚠⚠ **ONLY A SNAP THAT NAMES A FIXED POINT ON AN OBJECT.** NEAREST, PERPENDICULAR,
        // EXTENSION, TANGENT, PARALLEL and APPARENT all move WITH the cursor — acquiring one
        // tracks off an arbitrary spot that was never a feature of the drawing, which AutoCAD
        // never does. Measured: dragging toward a corner past an extension snap left a column
        // of seven acquisition crosses down the edge, every one of them meaningless.
        // ⚠ And two consecutive moves on the SAME point, which is the cheapest honest stand-in
        // for AutoCAD's hover: a cursor crossing a corner on its way somewhere else reports it
        // for one move and should not arm it.
        const d = dwell.current;
        dwell.current = d && Math.abs(d.x - sp.x) < 1e-9 && Math.abs(d.y - sp.y) < 1e-9
          ? { x: sp.x, y: sp.y, n: d.n + 1 } : { x: sp.x, y: sp.y, n: 1 };
        if (ACQUIRABLE.has(sp.kind) && dwell.current.n >= 2
            && !a.some((q) => Math.abs(q.x - sp.x) < 1e-9 && Math.abs(q.y - sp.y) < 1e-9)) {
          a.push({ x: sp.x, y: sp.y });
          if (a.length > 7) a.shift();          // AutoCAD's seven
          bump((n) => n + 1);
        }
        track.current = null;
      } else {
        dwell.current = null;
        const was = track.current;
        track.current = acquired.current.length ? trackAt(toModel(sx, sy)) : null;
        if (!!was !== !!track.current || (track.current && was
              && (was.pt.x !== track.current.pt.x || was.pt.y !== track.current.pt.y))) {
          bump((n) => n + 1);
        }
      }
    } else if (acquired.current.length || track.current) {
      dwell.current = null;
      // ⚠ Cleared the moment the command ends. Acquisitions that outlived their command would
      // put alignment paths on the NEXT one, running from corners the drafter no longer
      // remembers hovering — which reads as the cursor sticking for no reason.
      acquired.current = [];
      track.current = null;
      bump((n) => n + 1);
    }
    // ⚠ After the snap and before the band: a grip rollover is a cursor state, and it must not
    // survive into a drag — during a selection window there is no "the grip under the cursor".
    if (grips && !drag.current && !band.current?.live) {
      // ⚠⚠ **`nearestGrip` — THE SAME TEST THE CLICK USES.** A rollover computed with its own
      // tolerance would light a grip the click then misses, which is worse than no rollover:
      // the highlight would be a promise the mouse does not keep.
      const i = nearestGrip(sx, sy);
      const got = i >= 0 ? i : null;
      if (got !== hoverGrip.current) { hoverGrip.current = got; bump((n) => n + 1); }
    } else if (hoverGrip.current !== null) {
      hoverGrip.current = null;
      bump((n) => n + 1);
    }
    if (band.current) {
      band.current.x1 = sx;
      band.current.y1 = sy;
      // ⚠ A press only becomes a WINDOW once it has moved — see DRAG_PX. Latching `live` rather
      // than recomputing it means a drag that comes back to its start stays a window, which is
      // what AutoCAD does and what stops the band flickering out at the turn.
      if (!band.current.live
          && Math.hypot(sx - band.current.x0, sy - band.current.y0) > DRAG_PX) {
        band.current.live = true;
      }
    }
    // ⚠ The SNAPPED point when there is one. The coordinate box is what a drafter reads to
    // confirm the snap caught what they meant, and printing the raw pointer there would make it
    // disagree with the marker two pixels away.
    onCursor?.(snap.current ?? toModel(sx, sy), { x: sx, y: sy });
    bump((n) => n + 1);
  };

  /** ⚠⚠ **BUILT ON FIRST USE, NOT ON LOAD, AND CACHED AGAINST THE DISPLAY LIST ITSELF.**
   *  Indexing 165,000 segments costs real milliseconds; paying it while the drawing is still
   *  arriving would add it to the time-to-first-picture, which is the number a drafter judges
   *  the app by. Paying it on every mouse move would be worse still.
   *  ⚠ Keyed on the display-list OBJECT, so toggling a layer — which rebuilds it — rebuilds the
   *  index too. A snap that still fires on a layer you turned off is a snap you cannot trust. */
  const snapIndex = (): SnapIndex | null => {
    if (!dl) return null;
    if (index.current?.dl !== dl) index.current = { dl, ix: new SnapIndex(dl) };
    return index.current.ix;
  };

  const onUp = (e: React.PointerEvent) => {
    drag.current = null;
    // ⚠ The stroke ends when the button does. Reported before anything else so a freehand tool
    // never sees the selection machinery below it.
    if (trailing && e.button === 0) { onTrail?.(null); bump((n) => n + 1); return; }
    const b = band.current;
    band.current = null;
    if (!b || e.button !== 0) { bump((n) => n + 1); return; }
    // ⚠ An empty pick DESELECTS, and reporting the empty list rather than ignoring it is what
    // makes that work. AutoCAD clears the selection when you pick nothing, and a canvas that
    // only ever reports hits leaves a stale highlight nobody can get rid of.
    if (b.live) onSelect?.(hitRect(b.x0, b.y0, b.x1, b.y1, b.x1 < b.x0), e.shiftKey);
    else {
      const at = snap.current ?? toModel(b.x0, b.y0);
      // ⚠⚠ **A CLICK ON A GRIP IS NOT A SELECTION.** It is the start of a stretch, and AutoCAD
      // treats it that way — clicking the corner of a wall you have already picked grabs the
      // corner, it does not re-pick the wall. Tested BEFORE the entity hit test, or the object
      // under the grip wins every time.
      const gi = grips ? nearestGrip(b.x0, b.y0) : -1;
      if (gi >= 0) { onGrip?.(gi); bump((n) => n + 1); return; }
      // ⚠⚠ **CYCLING, AND THE STATE THAT MAKES IT WORK IS "THE SAME SPOT", NOT "THE SAME
      // OBJECT".** Clicking twice within a couple of pixels means "not that one, the next one";
      // clicking somewhere else is a fresh pick and must reset the counter, or the second click
      // anywhere in the drawing would silently select the object BEHIND what was aimed at.
      const all = hitAll(b.x0, b.y0);
      const same = cycle.current
                && Math.abs(cycle.current.x - b.x0) <= DRAG_PX
                && Math.abs(cycle.current.y - b.y0) <= DRAG_PX
                && cycle.current.n === all.length;
      const idx = same ? (cycle.current!.i + 1) % Math.max(1, all.length) : 0;
      cycle.current = all.length ? { x: b.x0, y: b.y0, i: idx, n: all.length } : null;
      const p = all[idx] ?? null;
      if (all.length > 1) onCycle?.(all, idx);
      onSelect?.(p ? [p] : [], e.shiftKey, at);
    }
    bump((n) => n + 1);
  };
  /** Double-click — AutoCAD's only gesture for opening the text editor.
   *
   * ⚠⚠⚠ **`detail` IS 0 ON A POINTER EVENT IN CHROMIUM, SO THE CLICK COUNT IS NOT THERE.**
   * The first version tested `e.detail >= 2` inside `onPointerUp` and it was never true —
   * measured: three pointerups in a row, every one `detail=0`, and one `dblclick`. Only the
   * `dblclick` event carries the gesture, so that is what this listens for.
   *
   * ⚠⚠ **AND THE TEXT IS FOUND BY ITS BOX, NOT BY `hit()`.** `polys` carries a decomposed
   * TEXT's STROKES, so a double-click that lands between two letters — which is most of the
   * area of a line of text — finds nothing at all and reads as "text cannot be edited".
   */
  const onDouble = (e: React.MouseEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const sx = e.clientX - r.left, sy = e.clientY - r.top;
    const t = hitText(sx, sy) ?? (() => { const p = hit(sx, sy);
                                          return p && TEXTUAL.has(p.kind) ? p : null; })();
    if (!t) return;
    e.preventDefault();
    onEditText?.(t, textBox(t.handle));
  };

  /** The middle and right buttons.
   *
   * ⚠⚠ **RIGHT-CLICK IS ENTER, AND ITS ABSENCE IS FELT WITHIN A MINUTE.** A drafter ends a
   * command with the right button far more often than with the ENTER key — it is the hand
   * already on the mouse. AutoCAD ships a shortcut menu on it and every fast drafter turns that
   * into ENTER; this does the second, which is unambiguous and is what the hand expects.
   *
   * ⚠ **A DOUBLE MIDDLE-CLICK IS ZOOM EXTENTS**, and it is tracked here rather than through
   * `onDoubleClick`: React's `dblclick` fires for the PRIMARY button only, so the wheel's
   * double-click never reaches it. The window is AutoCAD's own 500 ms.
   */
  const midClick = useRef(0);
  const onAux = (e: React.MouseEvent) => {
    if (e.button === 2) {
      e.preventDefault();
      onRightClick?.();
      return;
    }
    if (e.button === 1) {
      // ⚠ Only when the middle button did not PAN. `drag.current` is cleared on pointerup, so
      // the test is whether the view moved — a wheel-drag that panned must not also refit.
      const now = e.timeStamp;
      if (now - midClick.current < 500) { midClick.current = 0; onZoomExtentsGesture?.(); }
      else midClick.current = now;
    }
  };

  //: Where the last pick landed and how far through its candidate list we are — see `onUp`.
  const onLeave = () => {
    cursor.current = null;
    snap.current = null;
    onCursor?.(null);
    onSnap?.(null);
    bump((n) => n + 1);
  };

  return (
    <div
      ref={box}
      // ⚠⚠ **`none` ONLY WHERE THERE IS A DRAWING TO AIM AT.** The crosshair is drawn by this
      // component and replaces the pointer, which is right over geometry and wrong over the
      // empty state: `cursor` INHERITS, so the "No drawing open" panel and its Open button sat
      // inside a `cursor: none` box and the mouse simply vanished on the way to the button —
      // reappearing only over the button itself, which sets `pointer` of its own. Reported by
      // the owner, 2026-09-17: *"mouse will disappear when i try to move to that button"*.
      // ⚠ Keyed on `dl`, which is null exactly when the studio draws `Empty` over this box.
      style={{ position: "absolute", inset: 0, overflow: "hidden",
               background: dark ? ACAD_BG : "#fff", cursor: dl ? "none" : "default" }}
      onWheel={onWheel} onPointerDown={onDown} onPointerMove={onMove}
      onPointerUp={onUp} onPointerLeave={onLeave} onDoubleClick={onDouble}
      onAuxClick={onAux}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* ⚠⚠ **UNDER the 2D canvas, and the 2D canvas must stay TRANSPARENT for it to show.**
          An absolutely-positioned element paints above a static sibling whatever the DOM
          order, so the 2D canvas is positioned too and given the higher z-index. */}
      {gpu ? <canvas ref={gcv} data-testid="gpu-canvas"
                     style={{ position: "absolute", inset: 0, width: "100%", height: "100%",
                              display: "block", pointerEvents: "none", zIndex: 0 }} /> : null}
      <canvas ref={cv} style={{ width: "100%", height: "100%", display: "block",
                                position: "relative", zIndex: 1 }} />
    </div>
  );
}

/** Squared distance from a point to a segment. ⚠ SQUARED, and compared against a squared
 *  tolerance: a pick over 169,202 points is 169,200 of these, and `Math.sqrt` on every one is
 *  work with no effect on the answer. */
/** A CanvasGradient for one gradient-filled HATCH, across its own bounding box.
 *
 * ⚠⚠ **THE NINE DXF GRADIENTS ARE TWO CANVAS SHAPES AND A DIRECTION.** LINEAR and CURVED sweep
 * across; the SPHERICAL, HEMISPHERICAL and CYLINDER families radiate. The `INV` prefix is not a
 * tenth shape — it is the same gradient with its two colours swapped, which is exactly what
 * AutoCAD's "Invert" tick does. Treating them as nine unrelated cases is how eight of them end
 * up drawn as LINEAR.
 * ⚠ SCREEN space, per fill, per frame. The gradient has no extent of its own in DXF — AutoCAD
 * spreads it over the hatch's extents — so a gradient built once in drawing units would not
 * follow a zoom.
 * ⚠ A ONE-COLOUR gradient fades to the PAPER, not to black: `c2 === null` is the flag, and
 * `fallback` is the ink the fill would have had, used when the file names a gradient this
 * canvas does not know rather than dropping the fill entirely.
 */
function gradientStyle(
  ctx: CanvasRenderingContext2D,
  grad: readonly [string, string, string | null, number],
  pts: readonly number[], scale: number, ox: number, oy: number,
  fallback: string, paper: string,
): CanvasGradient | string {
  const [rawName, c1, c2] = grad;
  const name = String(rawName || "").toUpperCase();
  let lo = Infinity, hi = -Infinity, blo = Infinity, bhi = -Infinity;
  for (let i = 0; i < pts.length; i += 2) {
    const x = pts[i] * scale + ox, y = oy - pts[i + 1] * scale;
    if (x < lo) lo = x;
    if (x > hi) hi = x;
    if (y < blo) blo = y;
    if (y > bhi) bhi = y;
  }
  // ⚠ A fill smaller than a pixel gets the flat colour: `createLinearGradient` with two equal
  // points throws, and a hatch zoomed out to nothing is every hatch in a site plan.
  if (!Number.isFinite(lo) || hi - lo < 0.5 || bhi - blo < 0.5) return fallback;
  const inv = name.startsWith("INV");
  const base = inv ? name.slice(3) : name;
  const from = inv ? (c2 ?? paper) : c1;
  const to = inv ? c1 : (c2 ?? paper);
  let g: CanvasGradient;
  if (base === "SPHERICAL" || base === "HEMISPHERICAL" || base === "CYLINDER") {
    const cx = (lo + hi) / 2, cy = (blo + bhi) / 2;
    g = ctx.createRadialGradient(cx, cy, 0, cx, cy,
                                 Math.max(hi - lo, bhi - blo) / 2 || 1);
  } else {
    // ⚠ The sweep angle is DXF group 460, in radians, measured the DXF way (anticlockwise from
    // +X) — and the canvas Y axis points DOWN, so the sine is negated. Without that a 90°
    // gradient sweeps the wrong way, which reads as the drawing being upside down.
    const a = Number(grad[3]) || 0;
    const cx = (lo + hi) / 2, cy = (blo + bhi) / 2;
    const r = Math.max(hi - lo, bhi - blo) / 2 || 1;
    g = ctx.createLinearGradient(cx - Math.cos(a) * r, cy + Math.sin(a) * r,
                                 cx + Math.cos(a) * r, cy - Math.sin(a) * r);
  }
  g.addColorStop(0, from);
  g.addColorStop(1, to);
  return g;
}

function seg2(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const vx = bx - ax, vy = by - ay;
  const len = vx * vx + vy * vy;
  // ⚠ A zero-length segment is a real thing in a real drawing — a degenerate polyline vertex —
  // and dividing by its length gives NaN, which compares false against every tolerance and
  // makes the entity silently unpickable.
  const t = len > 0 ? Math.max(0, Math.min(1, ((px - ax) * vx + (py - ay) * vy) / len)) : 0;
  const dx = px - (ax + t * vx), dy = py - (ay + t * vy);
  return dx * dx + dy * dy;
}

/** AutoCAD's AutoSnap marker — a SHAPE per snap kind, plus the tooltip that names it.
 *
 * ⚠⚠ **THE SHAPE IS THE MESSAGE AND ONE COLOUR FOR ALL OF THEM WOULD NOT DO.** A drafter reads
 * "square" as endpoint and "triangle" as midpoint from the corner of their eye, at speed, without
 * reading the tooltip — that is the whole reason AutoCAD uses different glyphs rather than a dot
 * in different colours. It also survives being colour-blind, which a colour code does not.
 *
 * ⚠ The tooltip is drawn too, because the shapes are only obvious once you already know them.
 * AutoCAD shows both for the same reason.
 */
function drawSnapMarker(ctx: CanvasRenderingContext2D, x: number, y: number, kind: string) {
  //: AutoCAD's default AutoSnap marker colour. ⚠ Yellow on both a dark and a light background —
  //: it is drawn over the DRAWING, not over the page, so it does not follow the theme.
  const YEL = "#ffe93a";
  const s = 6;
  ctx.save();
  ctx.strokeStyle = YEL;
  ctx.lineWidth = 1.6;
  ctx.lineJoin = "miter";
  ctx.beginPath();
  const X = Math.round(x) + 0.5, Y = Math.round(y) + 0.5;
  switch (kind) {
    case "endpoint":
      ctx.rect(X - s, Y - s, s * 2, s * 2);
      break;
    case "midpoint":
      ctx.moveTo(X, Y - s - 1); ctx.lineTo(X + s + 1, Y + s); ctx.lineTo(X - s - 1, Y + s);
      ctx.closePath();
      break;
    case "centre":
      ctx.arc(X, Y, s, 0, Math.PI * 2);
      break;
    case "quadrant":
      ctx.moveTo(X, Y - s - 1); ctx.lineTo(X + s + 1, Y); ctx.lineTo(X, Y + s + 1);
      ctx.lineTo(X - s - 1, Y); ctx.closePath();
      break;
    case "intersection":
      ctx.moveTo(X - s, Y - s); ctx.lineTo(X + s, Y + s);
      ctx.moveTo(X + s, Y - s); ctx.lineTo(X - s, Y + s);
      break;
    case "perpendicular":
      // ⚠ AutoCAD's is a right-angle mark, not a full square: the two legs and the little tick
      // that says "90°". Drawing a square here would read as endpoint.
      ctx.moveTo(X - s, Y - s); ctx.lineTo(X - s, Y + s); ctx.lineTo(X + s, Y + s);
      ctx.moveTo(X - s, Y); ctx.lineTo(X, Y); ctx.lineTo(X, Y + s);
      break;
    case "nearest":
      ctx.moveTo(X - s, Y - s); ctx.lineTo(X + s, Y - s); ctx.lineTo(X - s, Y + s);
      ctx.lineTo(X + s, Y + s); ctx.closePath();
      break;
    default:                                   // extension — AutoCAD's dotted track marker
      ctx.moveTo(X - s, Y); ctx.lineTo(X + s, Y);
      ctx.moveTo(X, Y - s); ctx.lineTo(X, Y + s);
      break;
  }
  ctx.stroke();
  // ⚠ Offset down-right so the marker itself is never covered by its own label, and the label
  // never sits under the crosshair's arms.
  const label = kind === "centre" ? "Center" : kind[0].toUpperCase() + kind.slice(1);
  ctx.font = "11px system-ui";
  const w = ctx.measureText(label).width;
  ctx.fillStyle = "rgba(30,30,30,.92)";
  ctx.fillRect(X + s + 6, Y + s + 4, w + 10, 17);
  ctx.strokeStyle = "#6d6d6d";
  ctx.lineWidth = 1;
  ctx.strokeRect(X + s + 6.5, Y + s + 4.5, w + 10, 17);
  ctx.fillStyle = "#e8e8e8";
  ctx.fillText(label, X + s + 11, Y + s + 16);
  ctx.restore();
}

/** Does a segment touch an axis-aligned rectangle? ⚠ Both endpoints can be OUTSIDE and the
 *  segment still cross it — a wall running through a small crossing window — so a bounds test
 *  is not enough. Cohen–Sutherland outcodes answer it without computing an intersection. */
/** Do two segments cross? ⚠ The fence test, and it has to be a real crossing test rather than
 *  "is an endpoint near the fence": a fence drawn through the middle of a 30-metre wall touches
 *  neither of its ends, and catching that wall is the entire purpose of a fence. */
function segCross(ax: number, ay: number, bx: number, by: number,
                  cx: number, cy: number, dx: number, dy: number): boolean {
  const d1 = (dx - cx) * (ay - cy) - (dy - cy) * (ax - cx);
  const d2 = (dx - cx) * (by - cy) - (dy - cy) * (bx - cx);
  const d3 = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  const d4 = (bx - ax) * (dy - ay) - (by - ay) * (dx - ax);
  return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0));
}

/** Is a point inside a polygon? ⚠ Ray casting with a NON-STRICT test on one side only, so a
 *  vertex exactly on the ray is counted once rather than twice or not at all — the classic way
 *  a polygon selection misses objects that sit on its own corners. */
function inPoly(px: number, py: number, poly: readonly number[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 2; i < poly.length; j = i, i += 2) {
    const xi = poly[i], yi = poly[i + 1], xj = poly[j], yj = poly[j + 1];
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function segRect(ax: number, ay: number, bx: number, by: number,
                 lx: number, ly: number, hx: number, hy: number): boolean {
  const code = (x: number, y: number) =>
    (x < lx ? 1 : 0) | (x > hx ? 2 : 0) | (y < ly ? 4 : 0) | (y > hy ? 8 : 0);
  let ca = code(ax, ay), cb = code(bx, by);
  for (let guard = 0; guard < 8; guard++) {
    if (!(ca | cb)) return true;                 // both ends inside
    if (ca & cb) return false;                   // both ends on the same outside — no crossing
    const c = ca || cb;
    let x = 0, y = 0;
    if (c & 8) { x = ax + ((bx - ax) * (hy - ay)) / (by - ay); y = hy; }
    else if (c & 4) { x = ax + ((bx - ax) * (ly - ay)) / (by - ay); y = ly; }
    else if (c & 2) { y = ay + ((by - ay) * (hx - ax)) / (bx - ax); x = hx; }
    else { y = ay + ((by - ay) * (lx - ax)) / (bx - ax); x = lx; }
    if (c === ca) { ax = x; ay = y; ca = code(ax, ay); }
    else { bx = x; by = y; cb = code(bx, by); }
  }
  return false;
}

/** AutoCAD's full-width crosshair with its pickbox — not a pointer.
 *  ⚠ The crosshair spans the whole viewport on purpose: it is how a drafter lines a point up
 *  with something on the far side of the drawing without drawing a construction line. */
function drawCrosshair(ctx: CanvasRenderingContext2D, w: number, h: number,
                       c: { x: number; y: number }, dark: boolean) {
  ctx.save();
  ctx.strokeStyle = dark ? "rgba(255,255,255,.75)" : "rgba(0,0,0,.75)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, Math.round(c.y) + 0.5); ctx.lineTo(w, Math.round(c.y) + 0.5);
  ctx.moveTo(Math.round(c.x) + 0.5, 0); ctx.lineTo(Math.round(c.x) + 0.5, h);
  ctx.stroke();
  const p = 5;
  ctx.strokeRect(Math.round(c.x) - p + 0.5, Math.round(c.y) - p + 0.5, p * 2, p * 2);
  ctx.restore();
}

/** A grid that thins out as you zoom, the way AutoCAD's does.
 *  ⚠ Spacing is chosen in MODEL units and snapped to 1/2/5 x 10^n, so it stays a round number a
 *  drafter can count in — a grid at 37 mm is decoration, not a measuring aid. */
function drawGrid(ctx: CanvasRenderingContext2D, w: number, h: number, v: View, dark: boolean) {
  const target = 22;                                   // aim for a line every ~22 px
  const raw = target / v.scale;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / pow;
  const step = (n > 5 ? 10 : n > 2 ? 5 : n > 1 ? 2 : 1) * pow;
  const px = step * v.scale;
  if (px < 6) return;                                  // too dense to read — do not draw it
  const ox = w / 2 - v.x * v.scale;
  const oy = h / 2 + v.y * v.scale;
  ctx.save();
  ctx.lineWidth = 1;
  for (let major = 0; major < 2; major++) {
    const s = step * (major ? 10 : 1);
    const sp = s * v.scale;
    if (major && sp > Math.max(w, h) * 2) break;
    ctx.strokeStyle = dark
      ? (major ? "rgba(120,140,160,.30)" : "rgba(120,140,160,.13)")
      : (major ? "rgba(0,0,0,.22)" : "rgba(0,0,0,.09)");
    ctx.beginPath();
    const x0 = Math.floor((0 - ox) / sp) * sp + ox;
    for (let x = x0; x < w; x += sp) { ctx.moveTo(Math.round(x) + 0.5, 0); ctx.lineTo(Math.round(x) + 0.5, h); }
    const y0 = Math.floor((0 - oy) / sp) * sp + oy;
    for (let y = y0; y < h; y += sp) { ctx.moveTo(0, Math.round(y) + 0.5); ctx.lineTo(w, Math.round(y) + 0.5); }
    ctx.stroke();
  }
  ctx.restore();
  void rgbToCss;
}

/** Break an MTEXT into the lines it actually draws as.
 *
 * ⚠⚠ **WRAPPED HERE, WITH THE FONT THAT WILL DRAW IT.** The server knows the box width in
 * drawing units but has no idea how wide "OPEN PLAN OFFICE" is in the browser's Arial, so a
 * server-side wrap would break in a different place from the one that gets painted. `ctx` is
 * already carrying the exact font, so `measureText` here is the truth.
 *
 * ⚠ Hard breaks first, wrapping second — `\n` is the drafter's own decision and survives a box
 * wide enough to have held the line. `limit <= 0` means an MTEXT with no defined width, which
 * in DXF means "do not wrap".
 */
function wrapMText(ctx: CanvasRenderingContext2D, s: string, limit: number): string[] {
  const out: string[] = [];
  for (const para of s.split("\n")) {
    if (limit <= 0 || ctx.measureText(para).width <= limit) { out.push(para); continue; }
    let line = "";
    for (const word of para.split(/(\s+)/)) {
      if (!line) { line = word.trimStart(); continue; }
      if (ctx.measureText(line + word).width <= limit) { line += word; continue; }
      out.push(line.trimEnd());
      line = word.trimStart();
      // ⚠ A single word longer than the box still has to go somewhere. AutoCAD lets it run
      // over rather than hyphenating, and so does this — a broken part number is worse than
      // an overhang.
    }
    out.push(line.trimEnd());
  }
  return out.length ? out : [""];
}

/** The sheet, its shadow and the dashed printable area.
 *
 * ⚠ The margins come from the LAYOUT's own plot settings, not from a default. A drafter checking
 * whether a title block clears the printable area is checking against the plotter that sheet was
 * set up for; a dashed line drawn 10 mm in regardless would answer a question nobody asked.
 */
function drawSheet(ctx: CanvasRenderingContext2D, s: Sheet, v: View, ox: number, oy: number,
                  gpu = false) {
  const X = (x: number) => x * v.scale + ox;
  const Y = (y: number) => oy - y * v.scale;
  const [pw, ph] = s.paper;
  const [ml, mb, mr, mt] = s.margins;
  ctx.save();
  // ⚠⚠⚠ **THE PAPER IS OPAQUE AND THIS CANVAS SITS ON TOP OF THE GPU ONE.** Painting it here
  // while the GPU layer holds the linework covers the whole drawing: measured on sheet A-51-1 of
  // drawing 617, which came back as a blank page with a title block — all 2,981 of its polylines
  // drawn correctly, underneath. **No count could see it and the picture showed it at a glance.**
  // ⇒ With the GPU on, the paper and its shadow are drawn by the GPU layer — see `buildScene` —
  // and everything below here is hairline furniture that belongs on top either way.
  if (!gpu) {
    ctx.fillStyle = "rgba(0,0,0,.45)";
    ctx.fillRect(X(0) + 4, Y(ph) + 4, pw * v.scale, ph * v.scale);
    ctx.fillStyle = "#f2f2ef";
    ctx.fillRect(X(0), Y(ph), pw * v.scale, ph * v.scale);
  }
  ctx.strokeStyle = "#8a8a86";
  ctx.lineWidth = 1;
  ctx.strokeRect(X(0) + .5, Y(ph) + .5, pw * v.scale, ph * v.scale);
  if (ml + mr < pw && mb + mt < ph) {
    ctx.setLineDash([5, 4]);
    ctx.strokeStyle = "#9a9a94";
    ctx.strokeRect(X(ml) + .5, Y(ph - mt) + .5,
                   (pw - ml - mr) * v.scale, (ph - mb - mt) * v.scale);
    ctx.setLineDash([]);
  }
  // ⚠⚠ **THE WINDOW BORDERS ARE DRAWN HERE AND NOT SENT AS GEOMETRY.** A VIEWPORT's border is
  // screen furniture — AutoCAD shows it and does not plot it — so a border that arrived in the
  // display list as a polyline would be picked by a crossing, counted as an object and printed.
  ctx.strokeStyle = "rgba(120,120,120,.85)";
  for (const p of s.viewports) {
    ctx.strokeRect(X(p.c[0] - p.wh[0] / 2) + .5, Y(p.c[1] + p.wh[1] / 2) + .5,
                   p.wh[0] * v.scale, p.wh[1] * v.scale);
  }
  ctx.restore();
}

/** A paper layout: the sheet, and the windows cut in it. ⚠ The model seen THROUGH those windows
 *  is not here — it arrives composed into `polys`/`texts` in paper coordinates, owned by the
 *  viewport, because that is what makes a click through a window select the window the way
 *  AutoCAD does. See `_compose` in `display.py`. */
export interface Sheet {
  name: string;
  /** Width and height in millimetres, already turned the way the layout plots. */
  paper: [number, number];
  /** Left, bottom, right, top — the plotter's unprintable edge. */
  margins: [number, number, number, number];
  viewports: {
    h: string;
    c: [number, number];
    wh: [number, number];
    vc: [number, number];
    /** Paper per model — a 1:50 window is 0.02. */
    s: number;
    vh: number;
    twist: number;
    status: number;
    /** Layers frozen in THIS window only, by upper-case name. */
    off: string[];
    /** ⚠ Display locked — a drafter saying this sheet is at 1:50 and stays there. 311 of the
     *  1,803 viewports stored here have it, and VPSCALE refuses one rather than clearing it.
     *  Optional: a display list written before FORMAT 18 has no `lock`. */
    lock?: boolean;
    /** ⚠⚠ **THIS WINDOW'S OWN UCS** — `[originX, originY, xAxisDx, xAxisDy]`, or null for the
     *  world one. AutoCAD's UCSVP: two windows onto the same model at different rotations each
     *  need their own origin, and one drawing-wide UCS cannot say that.
     *  ⚠ `null` and "the world UCS" are DIFFERENT answers — a window reporting `[0,0,1,0]`
     *  would be indistinguishable from one a drafter had deliberately set to the world origin,
     *  and UCS Previous needs to tell them apart. FORMAT 27. */
    ucs?: [number, number, number, number] | null;
  }[];
}
